/* ==========================================================================
   BLVNKS — Start Production builder
   Client-side state machine for the four-step production request.

   Submission is isolated behind submitRun() at the bottom of this file.
   Until you point section setting "Submit endpoint" at your app proxy
   (e.g. /apps/blvnks/submit) the builder runs in demo mode: it assembles
   and logs the full payload without sending it anywhere.

   PRICING BELOW IS PLACEHOLDER. Replace the numbers in PRICING before going
   live. Note that these figures are only ever used to show the customer an
   indication — your app MUST recompute price server-side before creating the
   draft order, or a customer can edit these values in devtools.
   ========================================================================== */

(function () {
  'use strict';

  /* ----------------------------------------------------------- Placeholder
     rates. All money is in cents. */

  var PRICING = {
    /* Old heat-press volume rates. Not used for the in-house print charge.
       DTG tiers below are what the quote uses. */
    applicationTiers: [
      { min: 1, max: 11, cents: 400 },
      { min: 12, max: 24, cents: 350 },
      { min: 25, max: 49, cents: 325 },
      { min: 50, max: 99, cents: 300 },
      { min: 100, max: null, cents: 275 }
    ],

    samplePrice: 4500,
    maxStandardPlacements: 5,
    estimateSpread: 0.15,

    /* In-house DTG. Size comes from width and height. The rate comes from
       the total garment quantity. 50+ is quoted, not discounted further.
       Keep this identical to DTG_PRICING in blvnks-app/lib/pricing.js. */
    dtgQuoteMinQty: 50,
    dtgPrintTiers: [
      {
        name: 'Small',
        maxWidth: 5,
        maxHeight: 5,
        rates: [
          { minQty: 1, maxQty: 9, priceCents: 600 },
          { minQty: 10, maxQty: 24, priceCents: 550 },
          { minQty: 25, maxQty: 49, priceCents: 500 }
        ]
      },
      {
        name: 'Medium',
        maxWidth: 10,
        maxHeight: 10,
        rates: [
          { minQty: 1, maxQty: 9, priceCents: 900 },
          { minQty: 10, maxQty: 24, priceCents: 800 },
          { minQty: 25, maxQty: 49, priceCents: 750 }
        ]
      },
      {
        name: 'Large',
        maxWidth: 14,
        maxHeight: 16,
        rates: [
          { minQty: 1, maxQty: 9, priceCents: 1200 },
          { minQty: 10, maxQty: 24, priceCents: 1100 },
          { minQty: 25, maxQty: 49, priceCents: 1000 }
        ]
      },
      {
        name: 'Oversized',
        maxWidth: 16,
        maxHeight: 20,
        rates: [
          { minQty: 1, maxQty: 9, priceCents: 1500 },
          { minQty: 10, maxQty: 24, priceCents: 1400 },
          { minQty: 25, maxQty: 49, priceCents: 1300 }
        ]
      }
    ]
  };


  /* Placements that don't need a human to look at them. Anything outside
     this list routes the run to manual review. */
  var STANDARD_LOCATIONS = [
    'left-chest',
    'center-chest',
    'full-front',
    'upper-back',
    'full-back',
    'custom-front',
    'custom-back'
  ];

  var LOCATIONS = {
    'left-chest':   { side: 'Front', label: 'Left Chest',   view: 'front', maxIn: 5,    box: { x: 0.55, y: 0.30, w: 0.15, h: 0.15 } },
    'center-chest': { side: 'Front', label: 'Center Chest', view: 'front', maxIn: 12,   box: { x: 0.36, y: 0.30, w: 0.28, h: 0.20 } },
    'full-front':   { side: 'Front', label: 'Full Front',   view: 'front', maxIn: 14,   box: { x: 0.26, y: 0.26, w: 0.48, h: 0.46 } },
    'upper-back':   { side: 'Back',  label: 'Upper Back',   view: 'back',  maxIn: 12,   box: { x: 0.28, y: 0.20, w: 0.44, h: 0.17 } },
    'full-back':    { side: 'Back',  label: 'Full Back',    view: 'back',  maxIn: 14,   box: { x: 0.23, y: 0.21, w: 0.54, h: 0.54 } },
    'left-sleeve':  { side: 'Front', label: 'Left Sleeve',  view: 'front', maxIn: 4,    box: { x: 0.06, y: 0.27, w: 0.16, h: 0.14 } },
    'right-sleeve': { side: 'Front', label: 'Right Sleeve', view: 'front', maxIn: 4,    box: { x: 0.78, y: 0.27, w: 0.16, h: 0.14 } },
    'sleeve':       { side: 'Front', label: 'Sleeve',       view: 'front', maxIn: 4,    box: { x: 0.06, y: 0.27, w: 0.16, h: 0.14 }, legacy: true, alias: 'left-sleeve' },
    'neck':         { side: 'Back',  label: 'Neck Label',   view: 'back',  maxIn: 3.5,  box: { x: 0.41, y: 0.13, w: 0.18, h: 0.08 } },
    'custom-front': { side: 'Front', label: 'Custom',       view: 'front', maxIn: 14,   box: { x: 0.28, y: 0.28, w: 0.44, h: 0.44 } },
    'custom-back':  { side: 'Back',  label: 'Custom',       view: 'back',  maxIn: 14,   box: { x: 0.28, y: 0.28, w: 0.44, h: 0.44 } }
  };

  function canonicalLocationKey(key) {
    key = String(key || '');
    return key === 'sleeve' ? 'left-sleeve' : key;
  }

  function normalizePlacementLocation(p) {
    if (p && p.location) p.location = canonicalLocationKey(p.location);
    return p;
  }

  function isCustomLocationKey(key) {
    key = canonicalLocationKey(key);
    return key === 'custom-front' || key === 'custom-back';
  }

  function isSleeveLocationKey(key) {
    key = canonicalLocationKey(key);
    return key === 'left-sleeve' || key === 'right-sleeve';
  }

  function isPlacementDraggableKey(key) {
    return isCustomLocationKey(key) || isSleeveLocationKey(key);
  }

    var UNITS_PER_INCH = 52 / 21;

    var PRINT_SIZES = {
      small:     { label: 'Small',     inches: 4,    est: '~3" – 4.5" wide' },
      medium:    { label: 'Medium',    inches: 7,    est: '~6" – 8" wide' },
      large:     { label: 'Large',     inches: 10,   est: '~9" – 11" wide' },
      oversized: { label: 'Oversized', inches: 13.5, est: '~12" – 15" wide' }
    };


  /* Customer-facing placement choices stay intentionally small:
     Front and Back are free-placement canvases; Left Chest is the only
     convenience preset. This mirrors the simpler POD editor flow and keeps
     production-specific labels out of the customer's way. */
  var PLACEMENT_VIEWS = [
    { key: 'custom-front', label: 'Front', side: 'Front', widthIn: null },
    { key: 'custom-back',  label: 'Back', side: 'Back', widthIn: null },
    { key: 'left-chest',   label: 'Left Chest', side: 'Front', widthIn: 4 }
  ];

  /* Garment-specific production geometry. Shopify products opt into one of
     these profiles through custom.production_profile. Unknown or blank values
     safely fall back to tee. Limits are physical printable dimensions, not
     visual-only mockup dimensions. */
  var GARMENT_PROFILES = {
    tee: {
      label: 'Tee',
      frontPath: 'M31,7 L17,13 L4,31 L17,42 L24,35 L24,103 L76,103 L76,35 L83,42 L96,31 L83,13 L69,7 C65,16 58,20 50,20 C42,20 35,16 31,7 Z',
      backPath:  'M31,7 L17,13 L4,31 L17,42 L24,35 L24,103 L76,103 L76,35 L83,42 L96,31 L83,13 L69,7 C64,12 57,14 50,14 C43,14 36,12 31,7 Z',
      collarY: 18,
      custom: { front: { x: 24, y: 22, w: 52, h: 72 }, back: { x: 24, y: 18, w: 52, h: 76 } },

      /* Visual calibration for real Shopify flatlay photography. These values
         affect the preview/proof only; physical production limits stay below. */
      preview: {
        front: {
          /* Medium flat lay: chest 22.5 in, length 29.5 in.
             16 x 21 is the platen max, centered on the body, 2 in below the collar. */
          unitsPerInch: 2.161,
          collarY: 24,
          printArea: { x: 32.95, y: 28.32, w: 34.58, h: 45.38 },
          placements: {
            'left-chest':   { cx: 0.60, cy: 0.41, rotation: 0 },
            'center-chest': { cx: 0.50, cy: 0.39, rotation: 0 },
            'full-front':   { cx: 0.50, cy: 0.48, rotation: 0 },
            'left-sleeve':  { cx: 0.21, cy: 0.36, rotation: -22, track: { x1: 24, y1: 33, x2: 15, y2: 41, defaultT: 0.35 }, rotationMin: -35, rotationMax: -10 },
            'right-sleeve': { cx: 0.79, cy: 0.36, rotation: 22,  track: { x1: 76, y1: 33, x2: 85, y2: 41, defaultT: 0.35 }, rotationMin: 10,  rotationMax: 35 },
            'custom-front': { cx: 0.50, cy: 0.46, rotation: 0 }
          }
        },
        back: {
          unitsPerInch: 2.161,
          collarY: 24,
          printArea: { x: 32.95, y: 28.32, w: 34.58, h: 45.38 },
          placements: {
            'upper-back':  { cx: 0.50, cy: 0.32, rotation: 0 },
            'full-back':   { cx: 0.50, cy: 0.47, rotation: 0 },
            'neck':        { cx: 0.50, cy: 0.28, rotation: 0 },
            'custom-back': { cx: 0.50, cy: 0.45, rotation: 0 }
          }
        }
      },

      limits: {
        'left-chest': { w: 5, h: 6 }, 'center-chest': { w: 12, h: 7 },
        'full-front': { w: 14, h: 18 }, 'upper-back': { w: 12, h: 7 },
        'full-back': { w: 14, h: 19 }, 'left-sleeve': { w: 4, h: 5 }, 'right-sleeve': { w: 4, h: 5 }, 'sleeve': { w: 4, h: 5 },
        'neck': { w: 3.5, h: 2.5 }, 'custom-front': { w: 14, h: 18 },
        'custom-back': { w: 14, h: 19 }
      }
    },
    hoodie: {
      label: 'Pullover Hoodie',

      frontPath:
        'M34,15 ' +
        'C36,5 43,2 50,2 ' +
        'C57,2 64,5 66,15 ' +

        // right shoulder + long sleeve
        'L82,20 ' +
        'C85,28 89,40 93,51 ' +
        'L99,67 ' +
        'L86,73 ' +
        'L78,54 ' +
        'L76,44 ' +

        // body
        'L76,103 ' +
        'L24,103 ' +
        'L24,44 ' +

        // left long sleeve
        'L22,54 ' +
        'L14,73 ' +
        'L1,67 ' +
        'L7,51 ' +
        'C11,40 15,28 18,20 ' +

        // close shoulder
        'Z ' +

        // kangaroo pocket
        'M40,75 ' +
        'Q50,69 60,75 ' +
        'L64,94 ' +
        'L36,94 Z',

      backPath:
        'M34,15 ' +
        'C36,5 43,2 50,2 ' +
        'C57,2 64,5 66,15 ' +

        // right shoulder + long sleeve
        'L82,20 ' +
        'C85,28 89,40 93,51 ' +
        'L99,67 ' +
        'L86,73 ' +
        'L78,54 ' +
        'L76,44 ' +

        // body
        'L76,103 ' +
        'L24,103 ' +
        'L24,44 ' +

        // left long sleeve
        'L22,54 ' +
        'L14,73 ' +
        'L1,67 ' +
        'L7,51 ' +
        'C11,40 15,28 18,20 ' +
        'Z',

      collarY: 18,

      custom: {
        front: { x: 27, y: 23, w: 46, h: 48 },
        back:  { x: 24, y: 20, w: 52, h: 70 }
      },

      /* Visual calibration for the Heavy Hoodie flatlay family. */
      preview: {
        front: {
          unitsPerInch: 2.15,
          collarY: 30,
          /* Stops above the kangaroo pocket on the Heavy Hoodie front. */
          printArea: { x: 34, y: 34, w: 32, h: 31 },
          placements: {
            'left-chest':   { cx: 0.59, cy: 0.46, rotation: 0 },
            'center-chest': { cx: 0.50, cy: 0.46, rotation: 0 },
            'full-front':   { cx: 0.50, cy: 0.52, rotation: 0 },
            'left-sleeve':  { cx: 0.21, cy: 0.51, rotation: -40, track: { x1: 28, y1: 40, x2: 15, y2: 66, defaultT: 0.52 }, rotationMin: -55, rotationMax: -25 },
            'right-sleeve': { cx: 0.79, cy: 0.51, rotation: 40,  track: { x1: 72, y1: 40, x2: 85, y2: 66, defaultT: 0.52 }, rotationMin: 25,  rotationMax: 55 },
            'custom-front': { cx: 0.50, cy: 0.51, rotation: 0 }
          }
        },
        back: {
          unitsPerInch: 2.15,
          collarY: 30,
          printArea: { x: 32.8, y: 34, w: 34.4, h: 45.15 },
          placements: {
            'upper-back':  { cx: 0.50, cy: 0.39, rotation: 0 },
            'full-back':   { cx: 0.50, cy: 0.53, rotation: 0 },
            'neck':        { cx: 0.50, cy: 0.35, rotation: 0 },
            'custom-back': { cx: 0.50, cy: 0.52, rotation: 0 }
          }
        }
      },

      limits: {
        'left-chest':   { w: 5,   h: 6 },
        'center-chest': { w: 11,  h: 7 },
        'full-front':   { w: 12,  h: 14 },
        'upper-back':   { w: 12,  h: 7 },
        'full-back':    { w: 14,  h: 18 },
        'left-sleeve':  { w: 4,   h: 5 },
        'right-sleeve': { w: 4,   h: 5 },
        'sleeve':       { w: 4,   h: 5 },
        'neck':         { w: 3.5, h: 2.5 },
        'custom-front': { w: 12,  h: 14 },
        'custom-back':  { w: 14,  h: 18 }
      }
    },
    crewneck: {
      label: 'Crewneck',
      frontPath: 'M34,8 L18,15 L2,43 L16,52 L24,39 L24,103 L76,103 L76,39 L84,52 L98,43 L82,15 L66,8 C62,15 57,18 50,18 C43,18 38,15 34,8 Z',
      backPath:  'M34,8 L18,15 L2,43 L16,52 L24,39 L24,103 L76,103 L76,39 L84,52 L98,43 L82,15 L66,8 C61,12 56,14 50,14 C44,14 39,12 34,8 Z',
      collarY: 17,
      custom: { front: { x: 24, y: 22, w: 52, h: 70 }, back: { x: 24, y: 19, w: 52, h: 74 } },
      preview: {
        front: {
          /* Flat lay matches the small end of the crew chart (chest 22.5, length 27).
             16 in wide fits. Height stops at 19 in so the box stays under the collar and off the rib. */
          unitsPerInch: 1.997,
          printArea: { x: 33.34, y: 42.31, w: 31.95, h: 37.94 }
        },
        back: {
          unitsPerInch: 1.997,
          printArea: { x: 33.34, y: 42.31, w: 31.95, h: 37.94 }
        }
      },
      limits: {
        'left-chest': { w: 5, h: 6 }, 'center-chest': { w: 12, h: 7 },
        'full-front': { w: 14, h: 18 }, 'upper-back': { w: 12, h: 7 },
        'full-back': { w: 14, h: 19 }, 'sleeve': { w: 4, h: 5 },
        'neck': { w: 3.5, h: 2.5 }, 'custom-front': { w: 14, h: 18 },
        'custom-back': { w: 14, h: 19 }
      }
    },
    tank: {
      label: 'Tank',
      frontPath: 'M35,7 L25,12 L30,29 L26,103 L74,103 L70,29 L75,12 L65,7 C62,16 57,20 50,20 C43,20 38,16 35,7 Z',
      backPath:  'M35,7 L25,12 L30,29 L26,103 L74,103 L70,29 L75,12 L65,7 C61,13 56,15 50,15 C44,15 39,13 35,7 Z',
      collarY: 19,
      custom: { front: { x: 30, y: 24, w: 40, h: 68 }, back: { x: 30, y: 21, w: 40, h: 71 } },
      preview: {
        front: {
          unitsPerInch: 2.476,
          printArea: { x: 30.19, y: 24, w: 39.62, h: 52 }
        },
        back: {
          unitsPerInch: 2.476,
          printArea: { x: 30.19, y: 21, w: 39.62, h: 52 }
        }
      },
      limits: {
        'left-chest': { w: 4.5, h: 5 }, 'center-chest': { w: 10, h: 7 },
        'full-front': { w: 11, h: 17 }, 'upper-back': { w: 10, h: 7 },
        'full-back': { w: 11, h: 18 }, 'sleeve': { w: 0, h: 0 },
        'neck': { w: 3.5, h: 2.5 }, 'custom-front': { w: 11, h: 17 },
        'custom-back': { w: 11, h: 18 }
      }
    }
  };

  var SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL'];

    var SWATCHES = {
    'deep black':   '#111111',
    'black':        '#111111',
    'vintage grey': '#8e8d88',
    'bone':         '#e7e0d3',
    'white':        '#fbfbf9',
    'maroon':       '#5c2028',
    'navy':         '#1f2a44',
    'matcha':       '#a8b38a',
    'camo':         'linear-gradient(135deg,#6b6b4a 0 25%,#3d3d2b 25% 50%,#8a8a63 50% 75%,#4a4a33 75% 100%)'
  };

  function swatchFor(entry) {
    return entry.swatch || SWATCHES[String(entry.color).toLowerCase()] || '#d8d4cd';
  }

  function garmentProfile(key) {
    var normalized = String(key || 'tee').toLowerCase().replace(/[^a-z0-9-]/g, '');
    return GARMENT_PROFILES[normalized] || GARMENT_PROFILES.tee;
  }

  function profileKeyForProduct(product) {
    var explicit = String(product && product.production_profile || '')
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '');
    var hint = (
      (product && product.title ? product.title : '') + ' ' +
      (product && product.handle ? product.handle : '')
    ).toLowerCase();

    if (explicit && explicit !== 'tee' && GARMENT_PROFILES[explicit]) return explicit;
    if (hint.indexOf('hoodie') !== -1) return 'hoodie';
    if (hint.indexOf('crew') !== -1) return 'crewneck';
    if (hint.indexOf('tank') !== -1) return 'tank';
    return explicit || 'tee';
  }

  function garmentHex(entry) {
    var value = entry && entry.swatch ? entry.swatch : SWATCHES[String(entry && entry.color || '').toLowerCase()];
    if (typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value.trim())) return value.trim();
    if (String(entry && entry.color || '').toLowerCase() === 'camo') return '#5b5b3d';
    return '#d8d4cd';
  }

  function activeGarments() {
    return state.garments.filter(function (g) { return unitsFor(g) > 0; });
  }

  function garmentMockupImage(garment, loc) {
  if (!garment || !loc) return '';

  if (loc.view === 'back') {
    return garment.backImage || '';
  }

  return garment.frontImage || garment.image || '';
}

  function viewerGarment(p) {
    var garments = activeGarments();
    if (!garments.length) return state.garments[0] || null;
    if (p && p.previewKey) {
      var match = garments.find(function (g) {
        return String(g.productId) + '::' + String(g.color) === p.previewKey;
      });
      if (match) return match;
    }
    return garments[0];
  }

  function viewerKey(g) {
    return String(g.productId) + '::' + String(g.color);
  }

  /* ------------------------------------------------------------- Bootstrap */

  var root = document.querySelector('[data-blv-prod]');
  if (!root) return;

  var catalog = readJSON(root.querySelector('[data-blv-catalog]'));
  var cfg = readJSON(root.querySelector('[data-blv-config]'));
  if (!catalog || !cfg) return;

/* Print size no longer comes from the Ninja DTF app. Kept so the old
     limit checks stay inert instead of throwing. */
var NINJA = null;

    /* Config overrides, applied once at load. */
  if (cfg.applicationTiers && cfg.applicationTiers.length) {
    PRICING.applicationTiers = cfg.applicationTiers;
  }
    if (cfg.samplePriceCents) {
    PRICING.samplePrice = cfg.samplePriceCents;
  }

  var products = normalizeCatalog(catalog.products || []);

  /* Artwork services come from section blocks, so prices stay live and a new
     service is a block in the theme editor rather than a code change. The
     "not sure" option is always appended — a customer who can't diagnose their
     own file still needs a way through. */
  var services = (cfg.services || []).filter(function (s) {
    return s.title;
  }).concat([{
    id: 'not-sure',
    title: cfg.notSureLabel || 'Not sure what I need',
    description: cfg.notSureBody || "We'll review your file and recommend the right service.",
    productId: 0,
    handle: null,
    priceCents: 0,
    priceVaries: false,
    priceLabel: '',
    quoteOnly: true
  }]);

  var state = {
    step: 1,
    reached: 1,
    garments: [],
    artwork: { files: [], readiness: null, service: null },
    placements: [],
    placementProofs: [],
    applyToAll: true,
    orderType: null,
    sampleOf: [],
    contact: { email: '', name: '', phone: '' },
    notes: '',
    submitting: false,
    submitError: null,
    submitted: null
  };

  var el = {
    tracker: root.querySelector('[data-blv-tracker]'),
    steps: Array.prototype.slice.call(root.querySelectorAll('[data-blv-step]')),
    garments: root.querySelector('[data-blv-garments]'),
    productionMinimumNote: root.querySelector('[data-blv-production-minimum]'),
    productionProgress: root.querySelector('[data-blv-production-progress]'),
    summary: root.querySelector('[data-blv-summary]'),
    files: root.querySelector('[data-blv-files]'),
    readiness: root.querySelector('[data-blv-readiness]'),
    services: root.querySelector('[data-blv-services]'),
    helps: Array.prototype.slice.call(root.querySelectorAll('[data-blv-help]')),
    drop: root.querySelector('[data-blv-drop]'),
    input: root.querySelector('[data-blv-fileinput]'),
    placements: root.querySelector('[data-blv-placements]'),
    scopeNote: root.querySelector('[data-blv-scope-note]'),
    scopeToggle: root.querySelector('[data-blv-scope-toggle]'),
    paths: root.querySelector('[data-blv-paths]'),
    samplePick: root.querySelector('[data-blv-samplepick]'),
    notes: root.querySelector('[data-blv-notes]'),
    picker: root.querySelector('[data-blv-picker]'),
    pickerGrid: root.querySelector('[data-blv-picker-grid]'),
    pickerFilters: root.querySelector('[data-blv-picker-filters]'),
    review: root.querySelector('[data-blv-review]'),
    reviewBody: root.querySelector('[data-blv-review-body]'),
    reviewFoot: root.querySelector('[data-blv-review-foot]')
  };

  /* ------------------------------------------------------------- Utilities */

  function readJSON(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.error('[blvnks] bad JSON payload', e);
      return null;
    }
  }

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(cents) {
    var value = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: cfg.currency || 'USD',
        minimumFractionDigits: 2
      }).format(value);
    } catch (e) {
      return '$' + value.toFixed(2);
    }
  }

  function fileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  function uid() {
    return 'p' + Math.random().toString(36).slice(2, 9);
  }

  function icon(name, size) {
    var tpl = root.querySelector('[data-blv-svg="' + name + '"]');
    if (!tpl) return '';
    var s = size || 16;
    return tpl.innerHTML.replace('<svg', '<svg width="' + s + '" height="' + s + '"');
  }

  function productionBackImages(media) {
    var map = {};

    (media || []).forEach(function (item) {
      if (!item || !item.alt || !item.url) return;

      var match = String(item.alt).match(/^production-back\s*:\s*(.+)$/i);
      if (!match) return;

      var color = match[1].trim().toLowerCase();

      if (color) {
        map[color] = item.url;
      }
    });

    return map;
  }

  /* Turn Shopify's flat variant list into products grouped by colour. */
  function normalizeCatalog(list) {
    return list.map(function (p) {
      var names = (p.option_names || []).map(function (n) {
        return String(n).toLowerCase();
      });

      var ci = names.findIndex(function (n) {
        return /colou?r/.test(n);
      });

      var si = names.findIndex(function (n) {
        return /size/.test(n);
      });

      if (ci === -1) ci = 0;
      if (si === -1) si = names.length > 1 ? 1 : 0;

      var swatchMap = {};

      if (p.swatches && typeof p.swatches === 'object') {
        Object.keys(p.swatches).forEach(function (k) {
          swatchMap[k.toLowerCase()] = p.swatches[k];
        });
      }

      var backImages = productionBackImages(p.media);
      var byColor = {};

      (p.variants || []).forEach(function (v) {
        var opts = v.options || [];

        var color =
          ci === si
            ? (p.title || 'Default')
            : (opts[ci] || 'Default');

        var size = opts[si] || 'One size';

        if (!byColor[color]) {
          byColor[color] = {
            productId: p.id,
            productTitle: p.title,
            handle: p.handle,
            profile: profileKeyForProduct(p),
            color: color,
            swatch: swatchMap[String(color).toLowerCase()] || null,

            image: v.image || p.image || '',

            frontImage:
              v.mockup_image ||
              v.image ||
              p.image ||
              '',

            backImage:
              backImages[String(color).trim().toLowerCase()] ||
              '',

            sizes: []
          };
        }

        if (!byColor[color].image && v.image) {
          byColor[color].image = v.image;
        }

        if (!byColor[color].frontImage && (v.mockup_image || v.image)) {
          byColor[color].frontImage =
            v.mockup_image || v.image;
        }

        byColor[color].sizes.push({
          size: size,
          variantId: v.id,
          price: v.price,
          available: !!v.available
        });
      });

      var colors = Object.keys(byColor).map(function (k) {
        var entry = byColor[k];

        entry.sizes.sort(function (a, b) {
          var ai =
            SIZE_ORDER.indexOf(
              String(a.size).toUpperCase()
            );

          var bi =
            SIZE_ORDER.indexOf(
              String(b.size).toUpperCase()
            );

          if (ai === -1 && bi === -1) return 0;
          if (ai === -1) return 1;
          if (bi === -1) return -1;

          return ai - bi;
        });

        entry.available =
          entry.sizes.some(function (s) {
            return s.available;
          });

        return entry;
      });

      return {
        id: p.id,
        title: p.title,
        handle: p.handle,
        image: p.image,
        type: p.type || 'Other',
        profile: profileKeyForProduct(p),
        spec: p.spec || '',
        colors: colors
      };
    });
  }

  /* --------------------------------------------------------- Derived values */

  function unitsFor(garment) {
    return Object.keys(garment.qty).reduce(function (sum, k) {
      return sum + (garment.qty[k] || 0);
    }, 0);
  }

  function dominantSize(g) {
    var best = null;
    var bestQty = -1;
    g.sizes.forEach(function (s) {
      var q = g.qty[s.size] || 0;
      if (q > bestQty) { bestQty = q; best = s.size; }
    });
    return best || (g.sizes[0] && g.sizes[0].size) || null;
  }

  function sampleTotal() {
    return state.sampleOf.length * PRICING.samplePrice;
  }

  function garmentSubtotal(g) {
    return Object.keys(g.qty).reduce(function (sum, size) {
      var qty = g.qty[size] || 0;
      if (!qty) return sum;
      var row = g.sizes.find(function (s) { return s.size === size; });
      return sum + qty * (row ? row.price : 0);
    }, 0);
  }

  function totalUnits() {
    return state.garments.reduce(function (sum, g) { return sum + unitsFor(g); }, 0);
  }

  function applicationCount() {
    return totalUnits() * state.placements.length;
  }

  function dtgTierForSize(widthIn, heightIn) {
    var w = Number(widthIn);
    var h = Number(heightIn);
    if (!(w > 0) || !(h > 0)) return null;

    var match = null;
    PRICING.dtgPrintTiers.some(function (tier) {
      if (w <= tier.maxWidth + 0.001 && h <= tier.maxHeight + 0.001) {
        match = tier;
        return true;
      }
      return false;
    });
    return match;
  }

  function dtgCustomQuote() {
    return totalUnits() >= PRICING.dtgQuoteMinQty;
  }

  function dtgRateCents(tier, units) {
    if (!tier) return null;
    var qty = units == null ? totalUnits() : units;
    if (qty >= PRICING.dtgQuoteMinQty) return null;

    var rates = tier.rates || [];
    if (!qty) return rates.length ? rates[0].priceCents : null;

    var match = null;
    rates.forEach(function (rate) {
      if (qty >= rate.minQty && (rate.maxQty == null || qty <= rate.maxQty)) {
        match = rate.priceCents;
      }
    });
    return match;
  }

  function placementPrintDims(p) {
    if (!p) return null;
    if (p._liveWidthIn) return rawPlacementSize(p, null, p._liveWidthIn);
    return rawPlacementSize(p);
  }

  function placementDtgQuote(p) {
    var dims = placementPrintDims(p);
    if (!dims || dims.h == null) {
      return { dims: dims, tier: null, pending: true };
    }
    var tier = dtgTierForSize(dims.w, dims.h);
    return {
      dims: dims,
      tier: tier,
      rateCents: dtgRateCents(tier),
      custom: dtgCustomQuote(),
      pending: false
    };
  }

  function dtgTierMarkup(dims) {
    if (!dims || dims.h == null) {
      return 'Artwork size appears once the file can be measured.';
    }

    var sizeLine = 'Artwork size: ' +
      Number(dims.w).toFixed(1) + '" × ' +
      Number(dims.h).toFixed(1) + '"';
    var tier = dtgTierForSize(dims.w, dims.h);

    if (!tier) {
      return sizeLine +
        '<br>Larger than 16" × 20". This print needs a quote.';
    }

    var label = tier.name + ' DTG Print — up to ' +
      tier.maxWidth + '" × ' + tier.maxHeight + '"';

    if (dtgCustomQuote()) {
      return sizeLine + '<br>' + label +
        '<br>50+ garments — custom production pricing';
    }

    return sizeLine + '<br>' + label + ' — ' + money(dtgRateCents(tier));
  }

  function dtgEstimate() {
    var units = totalUnits();
    if (!units || dtgCustomQuote()) return 0;

    return state.placements.reduce(function (sum, placement) {
      var quote = placementDtgQuote(placement);
      if (quote.rateCents == null) return sum;
      return sum + quote.rateCents * units;
    }, 0);
  }

  function dtgSummaryRows() {
    var units = totalUnits();

    return state.placements.map(function (placement) {
      var quote = placementDtgQuote(placement);
      var place = esc(placementCustomerLabel(placement));

      if (!quote.dims || quote.dims.h == null) {
        return '<div class="blv-prod__sumtotal"><span>' + place +
          ' — artwork size pending</span><strong>—</strong></div>';
      }

      if (!quote.tier) {
        return '<div class="blv-prod__sumtotal"><span>' + place +
          ' — larger than 16" × 20"</span><strong>Quote</strong></div>';
      }

      var tier = quote.tier;

      if (quote.custom || quote.rateCents == null) {
        return '<div class="blv-prod__sumtotal"><span>' +
          esc(tier.name) + ' DTG Print — ' + place +
          ' — up to ' + tier.maxWidth + '" × ' + tier.maxHeight + '"' +
          '<span class="blv-prod__sumprint-size">Artwork size: ' +
          Number(quote.dims.w).toFixed(1) + '" × ' +
          Number(quote.dims.h).toFixed(1) +
          '" · 50+ garments — custom production pricing</span></span>' +
          '<strong>Quote</strong></div>';
      }

      var each = money(quote.rateCents);
      var extended = units ? money(quote.rateCents * units) : each;

      return '<div class="blv-prod__sumtotal"><span>' +
        esc(tier.name) + ' DTG Print — ' + place +
        ' — up to ' + tier.maxWidth + '" × ' + tier.maxHeight + '"' +
        '<span class="blv-prod__sumprint-size">Artwork size: ' +
        Number(quote.dims.w).toFixed(1) + '" × ' +
        Number(quote.dims.h).toFixed(1) + '" · ' + each +
        ' per garment</span></span><strong>' + extended + '</strong></div>';
    }).join('');
  }

  function dtgReviewRows() {
    var units = totalUnits();

    return state.placements.map(function (placement) {
      var quote = placementDtgQuote(placement);
      var place = esc(placementCustomerLabel(placement));

      if (!quote.dims || quote.dims.h == null || !quote.tier) {
        return '<dt>' + place + '</dt><dd>' +
          (quote.dims && quote.dims.h != null
            ? 'Larger than 16" × 20" — quoted'
            : 'Print price pending') +
          '</dd>';
      }

      var tier = quote.tier;

      if (quote.custom || quote.rateCents == null) {
        return '<dt>' + esc(tier.name) + ' DTG Print</dt><dd>' + place +
          '<br>Artwork size: ' +
          Number(quote.dims.w).toFixed(1) + '" × ' +
          Number(quote.dims.h).toFixed(1) + '"' +
          '<br>50+ garments — custom production pricing</dd>';
      }

      var total = units
        ? '<br>' + money(quote.rateCents) + ' × ' + units +
          ' = ' + money(quote.rateCents * units)
        : '<br>' + money(quote.rateCents) + ' per garment';

      return '<dt>' + esc(tier.name) + ' DTG Print</dt><dd>' + place +
        '<br>Artwork size: ' +
        Number(quote.dims.w).toFixed(1) + '" × ' +
        Number(quote.dims.h).toFixed(1) + '"' +
        '<br>Up to ' + tier.maxWidth + '" × ' + tier.maxHeight + '"' +
        total + '</dd>';
    }).join('');
  }

  function round2(n) { return Math.round(n * 100) / 100; }

  var MIN_EXACT_WIDTH_IN = 1;
  var EXACT_WIDTH_STEP_IN = 0.5;

  function presetWidth(p, sizeKey) {
    var size = PRINT_SIZES[sizeKey || p.size];
    return size ? size.inches : PRINT_SIZES.medium.inches;
  }

  function placementWidth(p) {
    var exact = Number(p && p.exactWidthIn);
    return isFinite(exact) && exact > 0 ? exact : presetWidth(p);
  }

  function rawPlacementSize(p, sizeKey, widthOverride) {
    var f = state.artwork.files.find(function (x) { return x.name === p.file; });
    var override = Number(widthOverride);
    var w;

    if (isFinite(override) && override > 0) {
      w = override;
    } else if (sizeKey) {
      w = presetWidth(p, sizeKey);
    } else {
      w = placementWidth(p);
    }

    var h = f && f.aspect ? round2(w / f.aspect) : null;
    return { w: round2(w), h: h };
  }

  function limitForGarment(g, location) {
    var profile = garmentProfile(g && g.profile);
    return profile.limits[location] || { w: LOCATIONS[location].maxIn || 99, h: 99 };
  }

  function dimensionsFit(p, dims) {
    if (!dims) return true;

    var garments = activeGarments();
    var fitsGarments = !garments.length || garments.every(function (g) {
      var lim = limitForGarment(g, p.location);
      if (!lim || lim.w <= 0 || lim.h <= 0) return false;
      if (dims.w > lim.w + 0.001) return false;
      if (dims.h != null && dims.h > lim.h + 0.001) return false;
      return true;
    });

    if (!fitsGarments) return false;

    if (NINJA && NINJA.limits) {
      if (NINJA.limits.maxWidthIn && dims.w > Number(NINJA.limits.maxWidthIn) + 0.001) return false;
      if (dims.h != null && NINJA.limits.maxHeightIn &&
          dims.h > Number(NINJA.limits.maxHeightIn) + 0.001) return false;
    }

    return true;
  }

  function placementFits(p, sizeKey) {
    return dimensionsFit(p, rawPlacementSize(p, sizeKey));
  }

  function placementFitsWidth(p, widthIn) {
    return dimensionsFit(p, rawPlacementSize(p, null, widthIn));
  }

  function largestFittingSize(p) {
    var keys = Object.keys(PRINT_SIZES);
    var fit = keys.filter(function (key) { return placementFits(p, key); });
    return fit.length ? fit[fit.length - 1] : 'small';
  }

  function maxPlacementWidth(p) {
    var max = Infinity;
    var file = state.artwork.files.find(function (x) { return x.name === p.file; });
    var aspect = file && file.aspect ? Number(file.aspect) : null;
    var garments = activeGarments();

    if (garments.length) {
      garments.forEach(function (g) {
        var lim = limitForGarment(g, p.location);
        if (!lim || lim.w <= 0 || lim.h <= 0) {
          max = 0;
          return;
        }
        max = Math.min(max, Number(lim.w));
        if (aspect && lim.h) max = Math.min(max, Number(lim.h) * aspect);
      });
    } else {
      max = Math.min(max, Number(LOCATIONS[p.location].maxIn || 99));
    }

    if (NINJA && NINJA.limits) {
      if (NINJA.limits.maxWidthIn) {
        max = Math.min(max, Number(NINJA.limits.maxWidthIn));
      }
      if (aspect && NINJA.limits.maxHeightIn) {
        max = Math.min(max, Number(NINJA.limits.maxHeightIn) * aspect);
      }
    }

    if (!isFinite(max)) max = Number(LOCATIONS[p.location].maxIn || 99);
    if (max <= 0) return 0;

    /* Keep the arrow controls on clean half-inch increments without ever
       stepping past a real garment/location limit. */
    return Math.floor((max + 0.0001) / EXACT_WIDTH_STEP_IN) * EXACT_WIDTH_STEP_IN;
  }

  function presetForWidth(widthIn) {
    var match = null;
    Object.keys(PRINT_SIZES).some(function (key) {
      if (Math.abs(PRINT_SIZES[key].inches - widthIn) < 0.001) {
        match = key;
        return true;
      }
      return false;
    });
    return match;
  }

  function setPlacementWidth(p, rawWidth) {
    if (!p) return;

    var max = maxPlacementWidth(p);
    if (max <= 0) return;

    var width = Number(rawWidth);
    if (!isFinite(width)) width = placementWidth(p);

    width = Math.round(width / EXACT_WIDTH_STEP_IN) * EXACT_WIDTH_STEP_IN;
    width = Math.max(Math.min(MIN_EXACT_WIDTH_IN, max), Math.min(max, width));
    width = round2(width);

    var preset = presetForWidth(width);
    if (preset && placementFits(p, preset)) {
      p.size = preset;
      p.exactWidthIn = null;
    } else {
      p.exactWidthIn = width;
    }

    refreshAutoCustomNote(p);
  }

  function normalizePlacementWidth(p) {
    if (!p) return;

    var exact = Number(p.exactWidthIn);
    if (isFinite(exact) && exact > 0) {
      setPlacementWidth(p, exact);
      return;
    }

    p.exactWidthIn = null;
    if (!placementFits(p, p.size)) p.size = largestFittingSize(p);
    refreshAutoCustomNote(p);
  }

  function placementSize(p) {
    var f = state.artwork.files.find(function (x) { return x.name === p.file; });
    if (!f || !f.aspect) return null;
    return rawPlacementSize(p);
  }

  function placementDims(p) {
    var s = placementSize(p);
    if (!s) {
      if (p.exactWidthIn != null) return placementWidth(p).toFixed(1) + '" wide';
      return PRINT_SIZES[p.size].est;
    }
    return s.w.toFixed(1) + '" × ' + s.h.toFixed(1) + '"';
  }

  function placementSizeLabel(p) {
    return p.exactWidthIn != null
      ? 'Custom width · ' + placementWidth(p).toFixed(1) + '"'
      : PRINT_SIZES[p.size].label;
  }

  function blanksEstimate() {
    var subtotal = 0;
    state.garments.forEach(function (g) {
      Object.keys(g.qty).forEach(function (size) {
        var qty = g.qty[size] || 0;
        if (!qty) return;
        var row = g.sizes.find(function (s) { return s.size === size; });
        subtotal += qty * (row ? row.price : 0);
      });
    });
    return subtotal;
  }

  /* Indicative only. The server recomputes all of this. */
  function estimate() {
    if (!totalUnits()) return 0;
    /* A sample charges for the sample garments only. The run is quoted
       separately once they've approved them. */
    if (state.orderType === 'sample') return sampleTotal();
    return blanksEstimate() + dtgEstimate();
  }

  /* Artwork services are a one-off charge on the run, not per garment, and
     quote-only services contribute nothing until we've looked at the file. */
  function artworkEstimate() {
    var svc = state.artwork.service;
    if (!svc || svc.quoteOnly || !svc.priceCents) return 0;
    return svc.priceCents;
  }

  function productionMinimum() {
    return Number(cfg.productionMinimumCents || 4000);
  }

  function minimumProductionUnits() {
    return Number(cfg.minimumProductionUnits || 4);
  }

  function productionQuantityEligible() {
    return totalUnits() >= minimumProductionUnits();
  }

function calculatedRunTotal() {
  return (
    blanksEstimate() +
    dtgEstimate() +
    artworkEstimate()
  );
}

function payableRunTotal() {
  var calculated = calculatedRunTotal();

  if (state.orderType === 'production') {
    return Math.max(calculated, productionMinimum());
  }

  if (state.orderType === 'sample') {
    return sampleTotal();
  }

  return calculated;
}

function productionMinimumApplies() {
  return (
    state.orderType === 'production' &&
    calculatedRunTotal() > 0 &&
    calculatedRunTotal() < productionMinimum()
  );
}

  /* Decide which track the run belongs on, and why. */
  function classify() {
    var reasons = [];

    if (state.artwork.readiness === 'unsure') {
      reasons.push('Artwork flagged for review before print');
    }
    if (state.artwork.readiness === 'help') {
      var svc = state.artwork.service;
      reasons.push(svc && svc.id !== 'not-sure'
        ? svc.title + ' requested'
        : 'Artwork help requested — service to be recommended');
    }
    if (!state.artwork.files.length) {
      reasons.push('No artwork files attached');
    }
    if (state.placements.length > PRICING.maxStandardPlacements) {
      reasons.push('More than ' + PRICING.maxStandardPlacements + ' print locations');
    }
    if (!state.applyToAll) {
      reasons.push('Print details vary by garment');
    }

    state.placements.forEach(function (p) {
      var loc = LOCATIONS[p.location];
      if (STANDARD_LOCATIONS.indexOf(p.location) === -1) {
        reasons.push(loc.label + ' is a non-standard placement');
      }
      if (p.location.indexOf('custom') === 0 && !String(p.note || '').trim()) {
        reasons.push('Custom placement with no description');
      }
    });
    if (state.placements.some(function (p) {
      var dims = rawPlacementSize(p);
      return !dims || dims.h == null;
    })) {
      reasons.push('Each print location needs a file we can measure before we can price it');
    }
    if (state.placements.some(function (p) {
      var quote = placementDtgQuote(p);
      return quote && !quote.pending && !quote.tier;
    })) {
      reasons.push('A print is larger than 16" × 20" and needs a custom quote');
    }
    if (dtgCustomQuote()) {
      reasons.push('50+ garments need custom production pricing');
    }

    return { standard: reasons.length === 0, reasons: reasons };
  }

  /* ------------------------------------------------------------- Validation */

  function stepComplete(n) {
    if (n === 1) return state.garments.length > 0 && totalUnits() > 0;
    if (n === 2) {
      if (state.artwork.readiness === null) return false;
      /* Asking for help without saying what kind leaves the run unpriceable. */
      if (state.artwork.readiness === 'help' && !state.artwork.service) return false;
      return true;
    }
    if (n === 3) return state.placements.length > 0;
    if (n === 4) {
      if (state.orderType === null) return false;

      if (
        state.orderType === 'production' &&
        !productionQuantityEligible()
      ) {
        return false;
      }

      if (
        state.orderType === 'sample' &&
        !state.sampleOf.length
      ) {
        return false;
      }

      /* The backend rejects a submission with no email, since a draft order
        has nowhere to send the invoice. Catch it here instead. */
      if (!/.+@.+\..+/.test(state.contact.email)) return false;

      return true;
    }
    return false;
  }

  function canSubmit() {
    return [1, 2, 3, 4].every(stepComplete) && !state.submitting;
  }

  /* ----------------------------------------------------------- Step control */

  function goTo(n) {
    state.step = n;
    state.reached = Math.max(state.reached, n);
    render();
    var target = el.steps[n - 1];
    if (target) {
      var y = target.getBoundingClientRect().top + window.pageYOffset - 24;
      window.scrollTo({
        top: y,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }
  }

  function advance(from) {
    if (!stepComplete(from)) return;
    if (from === 3) seedPlacements();
    goTo(Math.min(from + 1, 4));
  }

  /* ---------------------------------------------------------------- Render */

  function render() {
    renderTracker();
    renderStepChrome();
    renderGarments();
    renderProductionProgress();
    renderFiles();
    renderReadiness();
    renderServices();
    renderPlacements();
    renderScope();
    renderPaths();
    renderSamplePick();
    renderSummary();
    renderHelp();
    root.querySelectorAll('[data-blv-submit]').forEach(function (b) {
      b.disabled = !canSubmit();
    });
  }

  function renderTracker() {
    if (!el.tracker) return;
    Array.prototype.forEach.call(el.tracker.children, function (tab, i) {
      var n = i + 1;
      var done = stepComplete(n) && state.step !== n;
      tab.classList.toggle('is-active', state.step === n);
      tab.classList.toggle('is-done', done);
      tab.dataset.reachable = n <= state.reached ? 'true' : 'false';
      var dot = tab.querySelector('.blv-prod__dot');
      if (dot) dot.innerHTML = done ? icon('check', 12) : '0' + n;
    });
  }

  function renderStepChrome() {
    el.steps.forEach(function (step, i) {
      var n = i + 1;
      step.classList.toggle('is-active', state.step === n);
      step.classList.toggle('is-done', stepComplete(n));
      step.classList.toggle('is-locked', n > state.reached);
      var recap = step.querySelector('[data-blv-recap]');
      if (recap) recap.textContent = recapText(n);
      var next = step.querySelector('[data-blv-next]');
      if (next) next.disabled = !stepComplete(n);
    });
  }

  function recapText(n) {
    if (n === 1) {
      var units = totalUnits();
      return units
        ? units + ' units across ' + state.garments.length +
          (state.garments.length === 1 ? ' garment' : ' garments')
        : '';
    }
    if (n === 2) {
      var count = state.artwork.files.length;
      var label = { yes: 'Print-ready', unsure: 'Needs review', help: 'Artwork help' };
      var out = (count ? count + (count === 1 ? ' file' : ' files') : 'No files');
      if (state.artwork.readiness) out += ' · ' + label[state.artwork.readiness];
      if (state.artwork.readiness === 'help' && state.artwork.service) {
        out += ' · ' + state.artwork.service.title;
      }
      return out;
    }
    if (n === 3) {
      return state.placements.map(function (p) {
        return LOCATIONS[p.location].label + ' — ' + placementSizeLabel(p);
      }).join(' · ');
    }
    if (n === 4) {
      return state.orderType === 'sample' ? 'Sample first' : state.orderType === 'production' ? 'Full production' : '';
    }
    return '';
  }

  /* -------------------------------------------------------- Step 1 markup */

  function renderGarments() {
    if (!el.garments) return;

    if (!state.garments.length) {
      el.garments.innerHTML =
        '<div class="blv-prod__empty">No garments yet. Add a blank to start building your run.</div>';
      return;
    }

    el.garments.innerHTML = state.garments.map(function (g, gi) {
    var rows = g.sizes.map(function (s) {
      var qty = g.qty[s.size] || 0;
      return '' +
        '<div class="blv-prod__sizerow' + (s.available ? '' : ' is-oos') + '">' +
          '<span class="blv-prod__sizename">' + esc(s.size) + '</span>' +
          '<span class="blv-prod__sizeprice">' + money(s.price) + '</span>' +
          '<span class="blv-prod__qty">' +
            '<button type="button" data-blv-qty="dec" data-g="' + gi + '" data-size="' + esc(s.size) + '"' +
              (qty <= 0 ? ' disabled' : '') + ' aria-label="Remove one ' + esc(s.size) + '">&minus;</button>' +
            '<input type="number" min="0" step="1" value="' + qty + '" data-blv-qty="set" data-g="' + gi + '"' +
              ' data-size="' + esc(s.size) + '" aria-label="' + esc(g.productTitle + ' ' + g.color + ' size ' + s.size) + ' quantity"' +
              (s.available ? '' : ' disabled') + '>' +
            '<button type="button" data-blv-qty="inc" data-g="' + gi + '" data-size="' + esc(s.size) + '"' +
              (s.available ? '' : ' disabled') + ' aria-label="Add one ' + esc(s.size) + '">+</button>' +
          '</span>' +
        '</div>';
    }).join('');

      return '' +
        '<div class="blv-prod__garment">' +
          '<div class="blv-prod__gmedia">' +
            (g.image ? '<img src="' + esc(g.image) + '" alt="' + esc(g.productTitle + ' in ' + g.color) + '" loading="lazy">' : '') +
          '</div>' +
          '<div class="blv-prod__gbody">' +
            '<div class="blv-prod__gtop">' +
              '<div>' +
                '<h3 class="blv-prod__gname">' + esc(g.productTitle) + '</h3>' +
                '<p class="blv-prod__gcolor">' + esc(g.color) + '</p>' +
              '</div>' +
              '<button type="button" class="blv-prod__remove" data-blv-remove-garment="' + gi + '">Remove</button>' +
            '</div>' +
            '<div class="blv-prod__sizehead"><span>Size</span><span>Price</span><span>Qty</span></div>' +
            rows +
          '</div>' +
          '<div class="blv-prod__gtotal">' +
            '<span>Total</span>' +
            '<strong>' + unitsFor(g) + '</strong>' +
            '<span>units</span>' +
            (unitsFor(g) ? '<strong class="blv-prod__gsubtotal">' + money(garmentSubtotal(g)) + '</strong>' : '') +
          '</div>' +
        '</div>';
    }).join('');
  }

  function renderProductionProgress() {
    if (!el.productionProgress) return;

    var units = totalUnits();
    var minimum = minimumProductionUnits();

    if (!units) {
      el.productionProgress.hidden = true;
      el.productionProgress.textContent = '';
      el.productionProgress.classList.remove('is-qualified');
      return;
    }

    el.productionProgress.hidden = false;

    if (units < minimum) {
      var remaining = minimum - units;

      el.productionProgress.classList.remove('is-qualified');

      el.productionProgress.innerHTML =
        '<strong>Production starts at ' + minimum + ' units.</strong> ' +
        'Add ' + remaining + ' more ' +
        (remaining === 1 ? 'unit' : 'units') +
        ' to qualify, or continue to order samples.';
    } else {
      el.productionProgress.classList.add('is-qualified');

      el.productionProgress.innerHTML =
        '<strong>Production quantity reached.</strong> ' +
        'This run qualifies for full production.';
    }
  }

  /* -------------------------------------------------------- Step 2 markup */

  function renderFiles() {
    if (!el.files) return;
    if (!state.artwork.files.length) {
      el.files.innerHTML = '';
      return;
    }
    el.files.innerHTML = state.artwork.files.map(function (f, i) {
      var ext = f.name.split('.').pop().toUpperCase();
      var thumb = f.preview
        ? '<span class="blv-prod__thumb"><img src="' + f.preview + '" alt=""></span>'
        : '<span class="blv-prod__thumb blv-prod__thumb--none">' + esc(ext) + '</span>';

      return '' +
        '<li class="blv-prod__file">' +
          thumb +
          '<span class="blv-prod__filemeta">' +
            '<span class="blv-prod__filename">' + esc(f.name) + '</span>' +
            '<span class="blv-prod__filesize">' + fileSize(f.size) +
              (f.preview ? '' : ' · no preview available') +
            '</span>' +
          '</span>' +
          '<button type="button" class="blv-prod__remove" data-blv-remove-file="' + i + '">Remove</button>' +
        '</li>';
    }).join('');
  }

  function renderReadiness() {
    if (!el.readiness) return;
    Array.prototype.forEach.call(el.readiness.children, function (btn) {
      btn.classList.toggle('is-selected', btn.dataset.blvReadiness === state.artwork.readiness);
      btn.setAttribute('aria-pressed', btn.dataset.blvReadiness === state.artwork.readiness ? 'true' : 'false');
    });
  }

  function servicePriceLabel(svc) {
    if (svc.priceLabel) return svc.priceLabel;
    if (svc.quoteOnly || !svc.priceCents) return 'Request quote';
    return (svc.priceVaries ? 'From ' : 'Starting at ') + money(svc.priceCents);
  }

  /* Only rendered once they've said their artwork needs work. Selecting a
     service here records a request, not a purchase — the charge is confirmed
     and priced when the run is reviewed. */
  function renderServices() {
    if (!el.services) return;

    if (state.artwork.readiness !== 'help') {
      el.services.hidden = true;
      el.services.innerHTML = '';
      return;
    }

    el.services.hidden = false;

    var options = services.map(function (svc) {
      var selected = state.artwork.service && state.artwork.service.id === svc.id;
      return '' +
        '<button type="button" class="blv-prod__service' + (selected ? ' is-selected' : '') + '"' +
          ' data-blv-service="' + esc(svc.id) + '" aria-pressed="' + (selected ? 'true' : 'false') + '">' +
          '<span class="blv-prod__servicemark" aria-hidden="true"></span>' +
          '<span class="blv-prod__servicetext">' +
            '<strong>' + esc(svc.title) + '</strong>' +
            '<span>' + esc(svc.description) + '</span>' +
          '</span>' +
          '<span class="blv-prod__serviceprice">' + esc(servicePriceLabel(svc)) + '</span>' +
        '</button>';
    }).join('');

    el.services.innerHTML =
      '<p class="blv-prod__fieldlabel" style="margin-top:0">What kind of help do you need?</p>' +
      '<div class="blv-prod__servicelist">' + options + '</div>' +
      '<p class="blv-prod__hint" style="display:block;margin-top:12px">' +
        'Nothing is charged now. We confirm the right service and tier when we review your file, ' +
        'and the artwork charge appears on your quote.' +
      '</p>';
  }

  /* -------------------------------------------------------- Step 3 markup */

  /* Give the customer sensible starting rows rather than an empty step:
     one placement per uploaded file, defaulting front then back. */
  function seedPlacements() {
    if (state.placements.length) return;
    var defaults = ['left-chest', 'custom-back', 'custom-front'];
    var count = Math.max(1, Math.min(state.artwork.files.length, 3));
    for (var i = 0; i < count; i++) {
      state.placements.push({
        id: uid(),
        location: defaults[i] || 'custom-front',
        size: i === 0 ? 'medium' : 'oversized',
        exactWidthIn: null,
        file: state.artwork.files[i] ? state.artwork.files[i].name : null,
        note: '',
        noteAuto: true,
        rotationAdjust: 0
      });
    }
  }

  function contrastRatio(hexA, hexB) {
    function lum(hex) {
      var parts = [1, 3, 5].map(function (i) { return parseInt(hex.slice(i, i + 2), 16) / 255; });
      parts = parts.map(function (c) { return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); });
      return 0.2126 * parts[0] + 0.7152 * parts[1] + 0.0722 * parts[2];
    }
    var a = lum(hexA), b = lum(hexB);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  }

  function contrastNote(file, garment) {
    if (!file || !file.inkColor || !garment) return '';
    var ghex = garmentHex(garment);
    if (!/^#[0-9a-f]{6}$/i.test(ghex) || !/^#[0-9a-f]{6}$/i.test(file.inkColor)) return '';
    if (contrastRatio(file.inkColor, ghex) >= 2.2) return '';
    return 'Low contrast: the artwork and ' + garment.color + ' garment are similar in tone and may print as a subtle tonal effect.';
  }

  function customRegion(profile, loc) {
    return profile.custom[loc.view] || { x: 24, y: 22, w: 52, h: 70 };
  }

  function previewSideConfig(profile, loc) {
    if (!profile || !profile.preview || !loc) return null;
    return profile.preview[loc.view] || null;
  }

  function previewPlacementConfig(profile, loc, placementKey) {
    placementKey = canonicalLocationKey(placementKey);
    var side = previewSideConfig(profile, loc);
    if (!side || !side.placements) return null;
    return side.placements[placementKey] || null;
  }

  function previewUnitsPerInch(profile, loc) {
    var side = previewSideConfig(profile, loc);
    var units = side ? Number(side.unitsPerInch) : NaN;
    return isFinite(units) && units > 0 ? units : UNITS_PER_INCH;
  }

  function placementRegion(profile, loc, placementKey) {
    if (isCustomLocationKey(placementKey)) return customRegion(profile, loc);
    return null;
  }

  function sleeveTrack(profile, loc, placementKey) {
    var placement = previewPlacementConfig(profile, loc, placementKey);
    return placement && placement.track ? placement.track : null;
  }

  function clamp01(n) {
    return Math.max(0, Math.min(1, Number(n) || 0));
  }

  function sleeveTrackPoint(track, t) {
    t = clamp01(t);
    return {
      x: Number(track.x1) + (Number(track.x2) - Number(track.x1)) * t,
      y: Number(track.y1) + (Number(track.y2) - Number(track.y1)) * t
    };
  }

  function projectPointToSleeveTrack(track, x, y) {
    var x1 = Number(track.x1);
    var y1 = Number(track.y1);
    var dx = Number(track.x2) - x1;
    var dy = Number(track.y2) - y1;
    var denom = dx * dx + dy * dy;
    if (!denom) return 0;
    return clamp01(((x - x1) * dx + (y - y1) * dy) / denom);
  }

  function sleeveRotationInfo(p, profile, loc, placement) {
    var base = placement && isFinite(Number(placement.rotation)) ? Number(placement.rotation) : 0;
    var min = placement && isFinite(Number(placement.rotationMin)) ? Number(placement.rotationMin) : (base - 15);
    var max = placement && isFinite(Number(placement.rotationMax)) ? Number(placement.rotationMax) : (base + 15);
    var adjust = p && isFinite(Number(p.rotationAdjust)) ? Number(p.rotationAdjust) : 0;
    var value = Math.max(min, Math.min(max, base + adjust));
    adjust = value - base;
    if (p) p.rotationAdjust = adjust;
    return { base: base, min: min, max: max, value: value, adjust: adjust };
  }

  function effectivePlacementRotation(p, profile, loc, placement) {
    if (isSleeveLocationKey(p && p.location)) {
      return sleeveRotationInfo(p, profile, loc, placement).value;
    }
    return placement && isFinite(Number(placement.rotation)) ? Number(placement.rotation) : 0;
  }

  function canvasGeometry(p, file, garment) {
    normalizePlacementLocation(p);
    var loc = LOCATIONS[p.location];
    var profile = garmentProfile(garment && garment.profile);
    var b = loc.box;
    var placement = previewPlacementConfig(profile, loc, p.location);
    var unitsPerInch = previewUnitsPerInch(profile, loc);
    var dims = rawPlacementSize(p);

    var w = (dims ? dims.w : placementWidth(p)) * unitsPerInch;
    var aspect = file && file.aspect ? file.aspect : (b.w / b.h);
    var h = aspect ? w / aspect : w * (b.h / b.w);

    var presetCx = placement && isFinite(Number(placement.cx))
      ? Number(placement.cx) * 100
      : (b.x + b.w / 2) * 100;

    var presetCy = placement && isFinite(Number(placement.cy))
      ? Number(placement.cy) * 100
      : (b.y + b.h / 2) * 100;

    var rotation = effectivePlacementRotation(p, profile, loc, placement);
    var cx = presetCx;
    var cy = presetCy;

    if (isSleeveLocationKey(p.location)) {
      var track = sleeveTrack(profile, loc, p.location);
      if (track) {
        var sleeveT = isFinite(Number(p.sleeveT))
          ? clamp01(Number(p.sleeveT))
          : clamp01(track.defaultT == null ? 0.5 : track.defaultT);

        p.sleeveT = sleeveT;
        var trackPoint = sleeveTrackPoint(track, sleeveT);
        cx = trackPoint.x;
        cy = trackPoint.y;
      }
      rotation = effectivePlacementRotation(p, profile, loc, placement);
    } else if (isCustomLocationKey(p.location) && p.customPos) {
      var region = placementRegion(profile, loc, p.location);
      if (region) {
        var minX = region.x + w / 2;
        var maxX = region.x + region.w - w / 2;
        var minY = region.y + h / 2;
        var maxY = region.y + region.h - h / 2;

        cx = maxX >= minX
          ? Math.max(minX, Math.min(maxX, p.customPos.x))
          : region.x + region.w / 2;

        cy = maxY >= minY
          ? Math.max(minY, Math.min(maxY, p.customPos.y))
          : region.y + region.h / 2;

        p.customPos = { x: cx, y: cy };
      }
      rotation = 0;
    }

    /* Fabric editor state overrides the preset anchor once the customer has
       interacted with the artwork. Keep coordinates in the existing logical
       100 x 110 mockup space so proof generation can reuse them directly. */
    if (
      p.editor &&
      p.editor.view === loc.view &&
      isFinite(Number(p.editor.cx)) &&
      isFinite(Number(p.editor.cy))
    ) {
      cx = Number(p.editor.cx);
      cy = Number(p.editor.cy);

      if (isFinite(Number(p.editor.angle))) {
        rotation = Number(p.editor.angle);
      }
    }

    var x = cx - w / 2;
    var y = cy - h / 2;

    return {
      loc: loc,
      profile: profile,
      w: w,
      h: h,
      x: x,
      y: y,
      cx: cx,
      cy: cy,
      rotation: rotation,
      unitsPerInch: unitsPerInch
    };
  }

  function artGroupTransform(geo) {
    return 'translate(' + geo.cx.toFixed(3) + ' ' + geo.cy.toFixed(3) + ')' +
      ' rotate(' + geo.rotation.toFixed(3) + ')' +
      ' translate(' + (-geo.w / 2).toFixed(3) + ' ' + (-geo.h / 2).toFixed(3) + ')';
  }

  function cornerTicks(w, h) {
    var t = Math.min(3.5, Math.max(2, Math.min(w, h) * 0.16));
    return [
      'M0,' + t + ' V0 H' + t,
      'M' + (w - t) + ',0 H' + w + ' V' + t,
      'M0,' + (h - t) + ' V' + h + ' H' + t,
      'M' + (w - t) + ',' + h + ' H' + w + ' V' + (h - t)
    ].join(' ');
  }

  function placementCanvasSVG(p, i, file, garment) {
    var geo = canvasGeometry(p, file, garment);
    var loc = geo.loc;
    var profile = geo.profile;
    var garmentImage = garmentMockupImage(garment, loc);

    var fallbackFill = garmentHex(garment);
    var fallbackPath =
      loc.view === 'front'
        ? profile.frontPath
        : profile.backPath;
    var draggable = isPlacementDraggableKey(p.location);
    var art = file && file.preview
      ? '<image href="' + esc(file.preview) + '" x="0" y="0" width="' + geo.w.toFixed(2) + '" height="' + geo.h.toFixed(2) + '" preserveAspectRatio="xMidYMid meet"/>'
      : '';
    var groupClass = 'blv-prod__artgroup' + (draggable ? ' is-draggable' : '');
    
    var garmentLayer = garmentImage
    ? '<image class="blv-prod__canvas-garment-photo"' +
        ' href="' + esc(garmentImage) + '"' +
        ' x="0" y="0" width="100" height="110"' +
        ' preserveAspectRatio="xMidYMid meet"/>'
    : '<path class="blv-prod__canvas-garment"' +
        ' fill="' + esc(fallbackFill) + '"' +
        ' d="' + fallbackPath + '"/>';

      return '' +
        '<div class="blv-prod__canvaswrap">' +
          '<svg class="blv-prod__canvas" viewBox="0 0 100 110"' +
            ' xmlns="http://www.w3.org/2000/svg" role="img"' +
            ' aria-label="' +
              esc(loc.label + ' print preview, ' + placementSizeLabel(p)) +
            '">' +

            garmentLayer +

            '<g class="' + groupClass + '"' +
              ' data-blv-drag-art="' + i + '"' +
              ' data-w="' + geo.w.toFixed(4) + '"' +
              ' data-h="' + geo.h.toFixed(4) + '"' +
              ' transform="' + artGroupTransform(geo) + '">' +

              art +

              '<rect class="blv-prod__placement-box"' +
                ' x="0" y="0"' +
                ' width="' + geo.w.toFixed(2) + '"' +
                ' height="' + geo.h.toFixed(2) + '"/>' +

              '<path class="blv-prod__placement-ticks"' +
                ' d="' + cornerTicks(geo.w, geo.h) + '"/>' +

            '</g>' +
          '</svg>' +
        '</div>';
  }

  function remoteImageAsDataUrl(url) {
  if (!url) return Promise.resolve(null);

  return fetch(url)
    .then(function (response) {
      if (!response.ok) {
        throw new Error('Could not load garment image');
      }

      return response.blob();
    })
    .then(function (blob) {
      return new Promise(function (resolve, reject) {
        var reader = new FileReader();

        reader.onload = function () {
          resolve(reader.result);
        };

        reader.onerror = function () {
          reject(new Error('Could not read garment image'));
        };

        reader.readAsDataURL(blob);
      });
    });
}

  function fileAsDataUrl(file) {
    if (!file) return Promise.resolve(null);
    if (file.proofDataUrl) return Promise.resolve(file.proofDataUrl);
    if (file.raw) {
      return new Promise(function (resolve, reject) {
        var reader = new FileReader();
        reader.onload = function () {
          file.proofDataUrl = reader.result;
          resolve(reader.result);
        };
        reader.onerror = function () { reject(new Error('Could not read ' + file.name + ' for placement proof')); };
        reader.readAsDataURL(file.raw);
      });
    }
    if (file.preview && String(file.preview).indexOf('data:') === 0) return Promise.resolve(file.preview);
    return Promise.resolve(file.url || file.preview || null);
  }

  function placementProofSvg(
    p,
    file,
    garment,
    artHref,
    garmentHref
  ) {
    var geo = canvasGeometry(p, file, garment);
    var loc = geo.loc;
    var profile = geo.profile;

    var fallbackFill = garmentHex(garment);

    var fallbackPath =
      loc.view === 'front'
        ? profile.frontPath
        : profile.backPath;

    var garmentLayer = garmentHref
      ? '<image href="' + esc(garmentHref) + '"' +
          ' x="0" y="0" width="100" height="110"' +
          ' preserveAspectRatio="xMidYMid meet"/>'
      : '<path fill="' + esc(fallbackFill) + '"' +
          ' stroke="#c9c9c9"' +
          ' stroke-width="0.45"' +
          ' d="' + fallbackPath + '"/>';

    var art = artHref
      ? '<image href="' + esc(artHref) + '"' +
          ' x="0" y="0"' +
          ' width="' + geo.w.toFixed(2) + '"' +
          ' height="' + geo.h.toFixed(2) + '"' +
          ' preserveAspectRatio="xMidYMid meet"/>'
      : '';

    return '' +
      '<svg xmlns="http://www.w3.org/2000/svg"' +
        ' viewBox="0 0 100 110"' +
        ' width="900" height="990">' +
        '<rect width="100" height="110" fill="#f4f3f0"/>' +
        garmentLayer +
        '<g transform="' + artGroupTransform(geo) + '">' +
          art +
          '<rect x="0" y="0"' +
            ' width="' + geo.w.toFixed(2) + '"' +
            ' height="' + geo.h.toFixed(2) + '"' +
            ' fill="none"' +
            ' stroke="#737373"' +
            ' stroke-width="0.45"' +
            ' stroke-dasharray="1.6 1.25"/>' +
          '<path d="' + cornerTicks(geo.w, geo.h) + '"' +
            ' fill="none"' +
            ' stroke="#111111"' +
            ' stroke-width="0.6"/>' +
        '</g>' +
      '</svg>';
  }

  function svgMarkupToPng(svgMarkup) {
    return new Promise(function (resolve, reject) {
      var svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
      var objectUrl = URL.createObjectURL(svgBlob);
      var img = new Image();
      img.onload = function () {
        try {
          var canvas = document.createElement('canvas');
          canvas.width = 900;
          canvas.height = 990;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(objectUrl);
          canvas.toBlob(function (blob) {
            if (!blob) return reject(new Error('Could not create placement proof image'));
            resolve(blob);
          }, 'image/png', 0.95);
        } catch (err) {
          URL.revokeObjectURL(objectUrl);
          reject(err);
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Could not render placement proof'));
      };
      img.src = objectUrl;
    });
  }

  function safeProofName(value) {
    return String(value || 'proof')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'proof';
  }

  function generatePlacementProofs() {
    var garments = activeGarments();

    state.placementProofs = [];

    if (!state.placements.length || !garments.length) {
      return Promise.resolve();
    }

    var jobs = [];

    state.placements.forEach(function (p, pi) {
      var file = state.artwork.files.find(function (f) {
        return f.name === p.file;
      });

      garments.forEach(function (garment) {
        var loc = LOCATIONS[p.location];

        var garmentUrl =
          garmentMockupImage(garment, loc);

        jobs.push(
          Promise.all([
            fileAsDataUrl(file),
            remoteImageAsDataUrl(garmentUrl)
          ])
            .then(function (assets) {
              var artHref = assets[0];
              var garmentHref = assets[1];

              return svgMarkupToPng(
                placementProofSvg(
                  p,
                  file,
                  garment,
                  artHref,
                  garmentHref
                )
              );
            })
            .then(function (blob) {
              var dims = placementSize(p);

              state.placementProofs.push({
                placementId: p.id,
                placementIndex: pi,

                productId: garment.productId,
                productTitle: garment.productTitle,
                color: garment.color,
                profile: garment.profile || 'tee',

                location: p.location,
                locationLabel: loc.label,
                side: loc.side,

                size: p.size,

                sizeMode:
                  p.exactWidthIn != null
                    ? 'exact'
                    : 'preset',

                exactWidthIn:
                  p.exactWidthIn != null
                    ? placementWidth(p)
                    : null,

                widthIn:
                  dims ? dims.w : null,

                heightIn:
                  dims ? dims.h : null,

                note: p.note || null,

                name:
                  'placement-' +
                  (pi + 1) +
                  '-' +
                  safeProofName(garment.productTitle) +
                  '-' +
                  safeProofName(garment.color) +
                  '.png',

                type: 'image/png',
                sizeBytes: blob.size,
                blob: blob,
                storagePath: null,
                url: null
              });
            })
        );
      });
    });

    return Promise.all(jobs).then(function () {
      console.log(
        '[blvnks] generated placement proofs',
        state.placementProofs.length
      );
    });
  }

  function viewerControls(p, i, garment) {
    var garments = activeGarments();
    if (!garments.length || !garment) return '';
    var controls = '';
    if (garments.length > 1) {
      controls = '<div class="blv-prod__viewer-options">' + garments.map(function (g) {
        var key = viewerKey(g);
        return '<button type="button" class="blv-prod__viewer-chip' + (key === viewerKey(garment) ? ' is-active' : '') + '"' +
          ' data-blv-pview="' + i + '" data-view-key="' + esc(key) + '" aria-pressed="' + (key === viewerKey(garment)) + '">' +
          esc(g.color) + '</button>';
      }).join('') + '</div>';
    }
    return '<div class="blv-prod__viewer"><div class="blv-prod__viewer-label"><strong>Viewing:</strong> ' +
      esc(garment.productTitle + ' / ' + garment.color) + '</div>' + controls + '</div>';
  }

  /* -------------------------------------- Reusable artwork editor bridge
     All Fabric-specific interaction now lives in blvnks-artwork-editor.js.
     The Production Builder only translates its garment / artwork / placement
     state into editor configuration and consumes the editor callbacks. */

  var fabricEditors = [];

  function disposeFabricEditors() {
    fabricEditors.forEach(function (entry) {
      try {
        if (entry && typeof entry.destroy === 'function') {
          entry.destroy();
        }
      } catch (err) {
        console.warn(
          '[blvnks] editor dispose failed',
          err
        );
      }
    });

    fabricEditors = [];
  }

  function placementEditorMarkup(i, p, file, garment) {
    if (
      !window.fabric ||
      !window.BlvnksArtworkEditor ||
      !garment
    ) {
      return placementCanvasSVG(
        p,
        i,
        file,
        garment
      );
    }

    return '' +
      '<div class="blv-prod__canvaswrap">' +
        '<div class="blv-prod__fabricwrap"' +
          ' data-blv-fabric-editor="' + i + '">' +
          '<canvas data-blv-fabric-canvas="' + i + '"></canvas>' +
        '</div>' +
      '</div>' +
      '<p class="blv-prod__editor-hint">' +
        'Drag, resize, or rotate the artwork directly on the garment. ' +
        'The dashed box shows the printable area.' +
      '</p>';
  }

  function editorPrintRegion(profile, loc) {
    var side = previewSideConfig(
      profile,
      loc
    );

    if (
      side &&
      side.printArea &&
      isFinite(Number(side.printArea.x)) &&
      isFinite(Number(side.printArea.y)) &&
      isFinite(Number(side.printArea.w)) &&
      isFinite(Number(side.printArea.h))
    ) {
      return side.printArea;
    }

    return customRegion(
      profile,
      loc
    );
  }

  function normalizedSignedAngle(angle) {
    if (
      window.BlvnksArtworkEditor &&
      typeof window.BlvnksArtworkEditor.normalizeSignedAngle === 'function'
    ) {
      return window.BlvnksArtworkEditor.normalizeSignedAngle(
        angle
      );
    }

    angle = Number(angle) || 0;
    return ((angle + 180) % 360 + 360) % 360 - 180;
  }

  function updatePlacementRotationDisplay(index, rawAngle) {
  if (!el.placements) return;

  var output = el.placements.querySelector(
    '[data-blv-protate-value][data-p="' + index + '"]'
  );

  if (!output) return;

  var angle = normalizedSignedAngle(
    Number(rawAngle) || 0
  );

  angle = Math.round(angle * 10) / 10;

  /* Avoid displaying -0°. */
  if (Math.abs(angle) < 0.05) {
    angle = 0;
  }

  output.textContent = angle.toString() + '°';
}

  function updatePlacementDimensionDisplay(index, widthIn) {
    if (!el.placements) return;
    var placement = state.placements[index];
    if (!placement) return;

    var dims = rawPlacementSize(placement, null, widthIn);
    var widthInput = el.placements.querySelector(
      '[data-blv-pwidth][data-p="' + index + '"]'
    );
    var heightInput = el.placements.querySelector(
      '[data-blv-pheight][data-p="' + index + '"]'
    );

    if (widthInput && dims && dims.w != null) {
      widthInput.value = Number(dims.w).toFixed(1);
    }
    if (heightInput) {
      heightInput.value = dims && dims.h != null
        ? Number(dims.h).toFixed(1)
        : '—';
    }

    var tierNode = el.placements.querySelector(
      '[data-blv-pdtg][data-p="' + index + '"]'
    );
    if (tierNode) tierNode.innerHTML = dtgTierMarkup(dims);
  }

  function hydrateFabricEditors() {
    if (
      !window.fabric ||
      !window.BlvnksArtworkEditor ||
      !el.placements
    ) {
      return;
    }

    var nodes =
      el.placements.querySelectorAll(
        '[data-blv-fabric-editor]'
      );

    Array.prototype.forEach.call(
      nodes,
      function (wrap) {
        var i = parseInt(
          wrap.dataset.blvFabricEditor,
          10
        );

        var p = state.placements[i];
        if (!p) return;

        normalizePlacementLocation(p);

        var canvasEl =
          wrap.querySelector(
            '[data-blv-fabric-canvas]'
          );

        var garment = viewerGarment(p);

        var file =
          state.artwork.files.find(
            function (f) {
              return f.name === p.file;
            }
          );

        if (!canvasEl || !garment) return;

        var loc = LOCATIONS[p.location];
        var profile =
          garmentProfile(garment.profile);

        var geo =
          canvasGeometry(
            p,
            file,
            garment
          );

        var garmentUrl =
          garmentMockupImage(
            garment,
            loc
          );

        var instance =
          new window.BlvnksArtworkEditor({
            canvas: canvasEl,
            container: wrap,

            logicalWidth: 100,
            logicalHeight: 110,

            garmentUrl: garmentUrl,
            artworkUrl:
              file && file.preview
                ? file.preview
                : null,
            artworkName:
              file ? file.name : '',

            printArea:
              editorPrintRegion(
                profile,
                loc
              ),

            unitsPerInch:
              geo.unitsPerInch,

            minWidthIn:
              MIN_EXACT_WIDTH_IN,

            maxWidthIn:
              maxPlacementWidth(p),

            minAngle: -45,
            maxAngle: 45,

            state: {
              cx: geo.cx,
              cy: geo.cy,
              angle: geo.rotation || 0,
              widthIn: placementWidth(p)
            },

        onTransform:
          function (editorState, meta) {
            p.editor = {
              view: loc.view,
              cx: round2(editorState.cx),
              cy: round2(editorState.cy),
              angle: round2(editorState.angle)
            };

            updatePlacementRotationDisplay(
              i,
              editorState.angle
            );

            var liveAction =
              meta && meta.action
                ? String(meta.action)
                : '';

            if (
              liveAction.indexOf('scale') === 0 &&
              editorState.widthIn != null
            ) {
              p._liveWidthIn = editorState.widthIn;
              updatePlacementDimensionDisplay(
                i,
                editorState.widthIn
              );
              renderSummary();
            } else {
              p._liveWidthIn = null;
            }
          },

        onCommit:
          function (
            editorState,
            meta
          ) {
            p._liveWidthIn = null;
            p.editor = {
              view: loc.view,
              cx: round2(editorState.cx),
              cy: round2(editorState.cy),
              angle: round2(editorState.angle)
            };

            updatePlacementRotationDisplay(
              i,
              editorState.angle
            );

            var action =
              meta && meta.action
                ? String(meta.action)
                : '';

            if (
              action.indexOf('scale') === 0
            ) {
              setPlacementWidth(
                p,
                editorState.widthIn
              );

              updatePlacementDimensionDisplay(
                i,
                placementWidth(p)
              );

              renderSummary();
              renderStepChrome();

              return;
            }
          },

            onError:
              function (err) {
                console.warn(
                  '[blvnks] artwork editor error',
                  err
                );
              }
          });

        fabricEditors.push(instance);
      }
    );
  }

  function placementViewByKey(key) {
    key = canonicalLocationKey(key);

    return PLACEMENT_VIEWS.find(function (view) {
      return view.key === key;
    }) || null;
  }

  function applyPlacementView(p, key) {
    if (!p) return;

    key = canonicalLocationKey(key);
    var view = placementViewByKey(key);
    if (!view || !LOCATIONS[key]) return;

    var previousView = LOCATIONS[p.location] ? LOCATIONS[p.location].view : null;
    var nextView = LOCATIONS[key].view;

    p.location = key;
    p.editor = null;
    p.customPos = null;
    p.sleeveT = null;
    p.rotationAdjust = 0;
    p.noteAuto = true;

    /* Front/Back are views, not size presets. Preserve the customer's physical
       width when switching sides, subject to the new side's production limit.
       Left Chest intentionally starts at 4 inches. */
    if (view.widthIn != null) {
      setPlacementWidth(p, view.widthIn);
    } else {
      setPlacementWidth(p, placementWidth(p));
    }

    refreshAutoCustomNote(p);
  }

  function placementViewTabs(p, i) {
    var current = canonicalLocationKey(p.location);

    /* Legacy front/back location names still resolve to the closest simple
       customer-facing view if an in-progress placement reaches this build. */
    if (current !== 'left-chest' && current !== 'custom-back' && current !== 'custom-front') {
      current = LOCATIONS[current] && LOCATIONS[current].view === 'back'
        ? 'custom-back'
        : 'custom-front';
    }

    return '<div class="blv-prod__viewtabs" aria-label="Garment side">' +
      PLACEMENT_VIEWS.map(function (view) {
        var active = current === view.key;

        return '<button type="button" class="blv-prod__viewtab' +
          (active ? ' is-active' : '') + '"' +
          ' data-blv-pviewmode="' + esc(view.key) + '"' +
          ' data-p="' + i + '"' +
          ' aria-pressed="' + (active ? 'true' : 'false') + '">' +
            esc(view.label) +
          '</button>';
      }).join('') +
    '</div>';
  }

  function placementCustomerLabel(p) {
    var key = canonicalLocationKey(p.location);

    if (key === 'left-chest') return 'Left Chest';
    if (LOCATIONS[key] && LOCATIONS[key].view === 'back') return 'Back';
    return 'Front';
  }

  function placementCustomerSide(p) {
    return canonicalLocationKey(p.location) === 'left-chest'
      ? 'Front'
      : placementCustomerLabel(p);
  }

  function ensurePlacementEditorState(p) {
    if (!p) return null;

    normalizePlacementLocation(p);

    var loc = LOCATIONS[p.location];
    var garment = viewerGarment(p);
    var file = state.artwork.files.find(function (f) {
      return f.name === p.file;
    });
    var geo = canvasGeometry(p, file, garment);

    if (!p.editor || p.editor.view !== loc.view) {
      p.editor = {
        view: loc.view,
        cx: round2(geo.cx),
        cy: round2(geo.cy),
        angle: round2(geo.rotation || 0)
      };
    }

    return p.editor;
  }

  function placementRotation(p) {
    if (!p) return 0;

    var editorState = ensurePlacementEditorState(p);
    return editorState
      ? normalizedSignedAngle(editorState.angle || 0)
      : 0;
  }

  function setPlacementRotation(p, rawAngle) {
    if (!p) return;

    var editorState = ensurePlacementEditorState(p);
    if (!editorState) return;

    var angle = normalizedSignedAngle(rawAngle);
    angle = Math.max(-45, Math.min(45, angle));

    editorState.angle = round2(angle);
    p.editor = editorState;
  }

  function nudgePlacementRotation(p, delta) {
    if (!p) return;
    setPlacementRotation(
      p,
      placementRotation(p) + Number(delta || 0)
    );
  }

  function resetPlacementRotation(p) {
    setPlacementRotation(p, 0);
  }

  function renderPlacements() {
    if (!el.placements) return;

    disposeFabricEditors();

    if (!state.placements.length) {
      el.placements.innerHTML =
        '<div class="blv-prod__empty">No print locations yet. Add one to tell us where the artwork goes.</div>';
      return;
    }

    var fileOptions = state.artwork.files.map(function (f) {
      return { value: f.name, label: f.name };
    });

    el.placements.innerHTML = state.placements.map(function (p, i) {
      normalizePlacementLocation(p);
      var loc = LOCATIONS[p.location];
      var pf = state.artwork.files.find(function (f) { return f.name === p.file; });
      var garment = viewerGarment(p);
      if (garment && !p.previewKey) p.previewKey = viewerKey(garment);
      /* Editing Step 01, changing artwork, or changing location can introduce
         tighter physical limits. Keep preset and exact widths valid. */
      normalizePlacementWidth(p);

      var maxWidth = maxPlacementWidth(p);
      var currentWidth = placementWidth(p);
      var currentDims = rawPlacementSize(p);
      var currentHeight =
        currentDims && currentDims.h != null
          ? Number(currentDims.h)
          : null;
      var currentAngle = placementRotation(p);

      var dimensionControls =
        '<div class="blv-prod__editor-control-section">' +
          '<div class="blv-prod__editor-control-head">' +
            '<p class="blv-prod__eyebrow" style="margin:0">Size</p>' +
            '<span>Height stays proportional</span>' +
          '</div>' +
          '<div class="blv-prod__dimensions">' +
            '<label class="blv-prod__dimension">' +
              '<span class="blv-prod__dimension-key">W</span>' +
              '<input type="number" inputmode="decimal"' +
                ' min="' + MIN_EXACT_WIDTH_IN + '"' +
                ' max="' + (maxWidth > 0 ? maxWidth.toFixed(1) : '0') + '"' +
                ' step="' + EXACT_WIDTH_STEP_IN + '"' +
                ' value="' + currentWidth.toFixed(1) + '"' +
                ' data-blv-pwidth data-p="' + i + '"' +
                (maxWidth <= 0 ? ' disabled' : '') +
                ' aria-label="Artwork width in inches">' +
              '<span class="blv-prod__dimension-unit">in</span>' +
            '</label>' +
            '<label class="blv-prod__dimension">' +
              '<span class="blv-prod__dimension-key">H</span>' +
              '<input type="text"' +
                ' value="' + (currentHeight != null ? currentHeight.toFixed(1) : '—') + '"' +
                ' readonly tabindex="-1"' +
                ' data-blv-pheight data-p="' + i + '"' +
                ' aria-label="Artwork height in inches">' +
              '<span class="blv-prod__dimension-unit">in</span>' +
            '</label>' +
          '</div>' +
          '<p class="blv-prod__hint blv-prod__dimension-hint">' +
            (maxWidth > 0
              ? 'Resize on the garment or enter a width. Maximum for this placement: ' +
                maxWidth.toFixed(1) + ' in.'
              : 'This placement is not available for the selected garment.') +
          '</p>' +
          '<p class="blv-prod__dtg-tier" data-blv-pdtg data-p="' + i + '">' +
            dtgTierMarkup(currentDims) +
          '</p>' +
        '</div>';

      var rotationControls =
        '<div class="blv-prod__editor-control-section blv-prod__rotation-section">' +
          '<div class="blv-prod__editor-control-head">' +
            '<p class="blv-prod__eyebrow" style="margin:0">Rotation</p>' +
            '<button type="button" class="blv-prod__control-reset"' +
              ' data-blv-protate-reset data-p="' + i + '">' +
              'Reset' +
            '</button>' +
          '</div>' +
          '<div class="blv-prod__rotation-control">' +
            '<button type="button" data-blv-protate-step="-5" data-p="' + i + '"' +
              ' aria-label="Rotate artwork left 5 degrees">&minus;</button>' +
              '<output data-blv-protate-value data-p="' + i + '"' +
                ' aria-label="Current artwork rotation">' +
                esc((Math.round(currentAngle * 10) / 10).toString() + '°') +
              '</output>' +
            '<button type="button" data-blv-protate-step="5" data-p="' + i + '"' +
              ' aria-label="Rotate artwork right 5 degrees">+</button>' +
          '</div>' +
        '</div>';

      var locOptions = Object.keys(LOCATIONS).filter(function (key) {
        return !LOCATIONS[key].legacy && !isSleeveLocationKey(key);
      }).map(function (key) {
        return '<option value="' + key + '"' + (p.location === key ? ' selected' : '') + '>' +
          esc(LOCATIONS[key].side + ' — ' + LOCATIONS[key].label) + '</option>';
      }).join('');

      var fileSelect = fileOptions.length
        ? '<select class="blv-prod__select" data-blv-pfile data-p="' + i + '" aria-label="Artwork file for this location">' +
            '<option value="">Which file goes here?</option>' +
            fileOptions.map(function (o) {
              return '<option value="' + esc(o.value) + '"' + (p.file === o.value ? ' selected' : '') + '>' + esc(o.label) + '</option>';
            }).join('') +
          '</select>'
        : '';

      var note = contrastNote(pf, garment);

      return '' +
        '<div class="blv-prod__placement">' +
          '<div class="blv-prod__placement-preview">' +
            viewerControls(p, i, garment) +
            placementEditorMarkup(i, p, pf, garment) +
            placementViewTabs(p, i) +
            (note ? '<div class="blv-prod__contrast-note">' + icon('info', 16) + '<span>' + esc(note) + '</span></div>' : '') +
          '</div>' +
          '<div class="blv-prod__placement-controls">' +
            '<div class="blv-prod__placement-meta">' +
              '<div><span>' + esc(placementCustomerSide(p)) + '</span><strong>' +
                esc(canonicalLocationKey(p.location) === 'left-chest' ? 'LEFT CHEST' : 'ARTWORK') +
                '</strong>' +
                (p.file ? '<small>' + esc(p.file) + '</small>' : '') + '</div>' +
              '<button type="button" class="blv-prod__remove" data-blv-remove-placement="' + i + '">Remove</button>' +
            '</div>' +
            '<div class="blv-prod__placement-rule"></div>' +
            dimensionControls +
            rotationControls +
            '<div class="blv-prod__editor-control-section blv-prod__artwork-file-section">' +
              '<p class="blv-prod__eyebrow" style="margin:0 0 8px">Artwork</p>' +
              fileSelect +
            '</div>' +
          '</div>' +
        '</div>';
    }).join('');

    window.requestAnimationFrame(hydrateFabricEditors);
  }

  function renderScope() {
    if (!el.scopeNote) return;
    el.scopeNote.textContent = state.applyToAll
      ? 'These print details will be used for every garment in this run.'
      : 'Print details are being set per garment. We\'ll confirm the specifics with you during review.';
    if (el.scopeToggle) {
      el.scopeToggle.innerHTML =
        (state.applyToAll ? 'Customize by garment ' : 'Apply to all garments ') + icon('arrow-right', 13);
    }
  }

  /* -------------------------------------------------------- Step 4 markup */

  function renderPaths() {
    if (!el.paths) return;

    Array.prototype.forEach.call(el.paths.children, function (btn) {
      var path = btn.dataset.blvPath;
      var selected = path === state.orderType;

      if (path === 'production') {
        var eligible = productionQuantityEligible();

        btn.disabled = !eligible;
        btn.classList.toggle('is-disabled', !eligible);

        if (!eligible) {
          btn.setAttribute(
            'title',
            'Production starts at ' + minimumProductionUnits() + ' units.'
          );
        } else {
          btn.removeAttribute('title');
        }

        if (!eligible && state.orderType === 'production') {
          state.orderType = null;
          selected = false;
        }
      }

      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    if (el.productionMinimumNote) {
      if (productionQuantityEligible()) {
        el.productionMinimumNote.textContent =
          'Production minimum: ' + minimumProductionUnits() + ' units.';
      } else {
        el.productionMinimumNote.textContent =
          'Production starts at ' +
          minimumProductionUnits() +
          ' units. Need fewer? Order samples instead.';
      }
    }
  }

    /* Revealed once they choose the sample path. One row per garment: sample it
     or not, and in which size. Defaults to the size they ordered most of. */
  function renderSamplePick() {
    if (!el.samplePick) return;

    if (state.orderType !== 'sample') {
      el.samplePick.hidden = true;
      el.samplePick.innerHTML = '';
      return;
    }

    el.samplePick.hidden = false;

    var rows = state.garments.map(function (g, gi) {
      var picked = state.sampleOf.find(function (s) { return s.garment === gi; });
      var sizeOptions = g.sizes.map(function (s) {
        return '<option value="' + esc(s.size) + '"' +
          (picked && picked.size === s.size ? ' selected' : '') +
          (s.available ? '' : ' disabled') + '>' + esc(s.size) + '</option>';
      }).join('');

      return '' +
        '<div class="blv-prod__samplerow' + (picked ? ' is-selected' : '') + '">' +
          '<button type="button" class="blv-prod__samplecheck" data-blv-sample-toggle="' + gi + '"' +
            ' aria-pressed="' + (picked ? 'true' : 'false') + '"' +
            ' aria-label="Sample ' + esc(g.productTitle + ' in ' + g.color) + '"></button>' +
          '<div class="blv-prod__samplemedia">' +
            (g.image ? '<img src="' + esc(g.image) + '" alt="" loading="lazy">' : '') +
          '</div>' +
          '<div class="blv-prod__sampletext">' +
            '<strong>' + esc(g.productTitle) + '</strong>' +
            '<span>' + esc(g.color) + '</span>' +
          '</div>' +
          '<select class="blv-prod__select blv-prod__samplesize" data-blv-sample-size="' + gi + '"' +
            (picked ? '' : ' disabled') + ' aria-label="Sample size">' +
            sizeOptions +
          '</select>' +
        '</div>';
    }).join('');

    var count = state.sampleOf.length;

    el.samplePick.innerHTML =
      '<p class="blv-prod__fieldlabel" style="margin-top:0">Which do you want sampled?</p>' +
      '<div class="blv-prod__samplelist">' + rows + '</div>' +
      '<p class="blv-prod__samplesum">' +
        (count
          ? count + (count === 1 ? ' sample' : ' samples') + ' · ' + money(sampleTotal()) +
            ', charged today. Your full run stays a quote until you approve them.'
          : 'Pick at least one garment to sample.') +
      '</p>';
  }

  /* ----------------------------------------------------------- Run summary */

  function renderSummary() {
    if (!el.summary) return;
    var units = totalUnits();

    if (!state.garments.length) {
      el.summary.innerHTML =
        '<div class="blv-prod__panelhead"><h3>Run summary</h3></div>' +
        '<p class="blv-prod__count">Add a garment and your run will build here.</p>';
      return;
    }

    var lines = state.garments.map(function (g) {
      var breakdown = g.sizes
        .filter(function (s) { return (g.qty[s.size] || 0) > 0; })
        .map(function (s) { return s.size + ' ' + g.qty[s.size]; })
        .join(' / ');

      return '' +
        '<div class="blv-prod__sumline">' +
          '<div class="blv-prod__summedia">' +
            (g.image ? '<img src="' + esc(g.image) + '" alt="" loading="lazy">' : '') +
          '</div>' +
          '<div>' +
            '<h4>' + esc(g.productTitle) + '</h4>' +
            '<p>' + esc(g.color) + '</p>' +
            '<p class="blv-prod__units">' + unitsFor(g) + ' units</p>' +
            (breakdown ? '<p class="blv-prod__sumbreak">' + esc(breakdown) + '</p>' : '') +
          '</div>' +
        '</div>';
    }).join('');

    var verdict = classify();
    var priceRow = '';
    var sampleRow = '';

    if (units && state.placements.length) {
      /* The rail always describes the run. A sample is an additional line,
         not a replacement — the component rows above are run figures and a
         total smaller than any of them reads as a bug. */
      var runValue = calculatedRunTotal();
      var payableValue = payableRunTotal();
      var minimumApplies = productionMinimumApplies();

    if (dtgCustomQuote()) {
      priceRow =
        '<div class="blv-prod__sumtotal blv-prod__sumtotal--due">' +
          '<span>50+ garments</span>' +
          '<strong>Custom production pricing</strong>' +
        '</div>';
    } else if (verdict.standard) {
      if (minimumApplies) {
        priceRow =
          '<div class="blv-prod__sumtotal">' +
            '<span>Calculated subtotal</span>' +
            '<strong>' + money(runValue) + '</strong>' +
          '</div>' +

          '<div class="blv-prod__sumtotal blv-prod__sumtotal--minimum">' +
            '<span>Production minimum</span>' +
            '<strong>' + money(productionMinimum()) + '</strong>' +
          '</div>' +

          '<div class="blv-prod__sumtotal blv-prod__sumtotal--due">' +
            '<span>Estimated total</span>' +
            '<strong>' + money(payableValue) + '</strong>' +
          '</div>';
      } else {
        priceRow =
          '<div class="blv-prod__sumtotal blv-prod__sumtotal--due">' +
            '<span>Estimated total</span>' +
            '<strong>' + money(payableValue) + '</strong>' +
          '</div>';
      }
    } else {
      var low = Math.round(runValue * (1 - PRICING.estimateSpread));
      var high = Math.round(runValue * (1 + PRICING.estimateSpread));

      priceRow =
        '<div class="blv-prod__sumtotal"><span>Estimated run</span><strong>' +
        money(low) + ' – ' + money(high) + '</strong></div>';
    }

      if (state.orderType === 'sample' && state.sampleOf.length) {
        sampleRow =
          '<div class="blv-prod__sumtotal blv-prod__sumtotal--due">' +
          '<span>' + state.sampleOf.length +
          (state.sampleOf.length === 1 ? ' sample' : ' samples') +
          ' — due today</span><strong>' + money(sampleTotal()) + '</strong></div>';
      }
    }

    var note;
    if (state.orderType === 'sample' && state.sampleOf.length) {
      note = 'Only the samples are charged today. Your run stays a quote until you approve them.';
    } else if (dtgCustomQuote()) {
      note = '50+ garments use custom production pricing. Contact us and we will quote the run. Nothing is charged yet.';
    } else if (verdict.standard && units && state.placements.length) {
      note = 'This run is below our production minimum. This is not an additional setup fee.';
    } else {
      note = 'Pricing will be confirmed after we review your artwork and production details.';
    }
    var blanksRow = '';
    if (units) {
      blanksRow =
        '<div class="blv-prod__sumtotal"><span>Blanks — ' + units +
        (units === 1 ? ' garment' : ' garments') + '</span>' +
        '<strong>' + money(blanksEstimate()) + '</strong></div>';
    }

    var appsRow = state.placements.length ? dtgSummaryRows() : '';

    var transferRow = '';

    var artworkRow = '';
    if (state.artwork.service) {
      artworkRow =
        '<div class="blv-prod__sumtotal"><span>Artwork — ' + esc(state.artwork.service.title) + '</span>' +
        '<strong>' + esc(state.artwork.service.quoteOnly || !state.artwork.service.priceCents
          ? 'Quoted'
          : 'from ' + money(state.artwork.service.priceCents)) + '</strong></div>';
    }

    el.summary.innerHTML =
      '<div class="blv-prod__panelhead">' +
        '<h3>Run summary</h3>' +
        '<span class="blv-prod__count">' + state.garments.length +
          (state.garments.length === 1 ? ' item' : ' items') + '</span>' +
      '</div>' +
      lines +
      '<div class="blv-prod__sumtotal"><span>Total quantity</span><strong>' + units + ' units</strong></div>' +
      blanksRow +
      appsRow +
      transferRow +
      artworkRow +
      priceRow +
      sampleRow +
      '<div class="blv-prod__notice">' + icon('info', 14) + '<span>' + note + '</span></div>';
  }

  /* The rail carries one contextual box at a time, matched to the open step.
     Step 1 has none — the run summary above it is already the relevant panel. */
  function renderHelp() {
    el.helps.forEach(function (box) {
      box.hidden = parseInt(box.dataset.blvHelp, 10) !== state.step;
    });
  }

  /* -------------------------------------------------------- Garment picker */

  var pickerState = { filter: 'all', color: {} };

    function openPicker() {
    renderPicker();
    openModal(el.picker);
  }

   function renderPicker() {
    if (!el.pickerGrid) return;

    if (!products.length) {
      el.pickerGrid.innerHTML =
        '<p class="blv-prod__count">No blanks found. Pick a collection in the theme editor for this section.</p>';
      if (el.pickerFilters) el.pickerFilters.innerHTML = '';
      return;
    }

    /* Filter bar only earns its space once there's more than one type. */
    if (el.pickerFilters) {
      var types = [];
      products.forEach(function (p) {
        if (types.indexOf(p.type) === -1) types.push(p.type);
      });
      el.pickerFilters.innerHTML = types.length > 1
        ? ['all'].concat(types).map(function (t) {
            var label = t === 'all' ? 'All' : t;
            return '<button type="button" class="blv-prod__filter' +
              (pickerState.filter === t ? ' is-selected' : '') + '"' +
              ' data-blv-filter="' + esc(t) + '">' + esc(label) + '</button>';
          }).join('')
        : '';
    }

    var visible = products.filter(function (p) {
      return pickerState.filter === 'all' || p.type === pickerState.filter;
    });

    el.pickerGrid.innerHTML = visible.map(function (p) {
      var chosen = pickerState.color[p.id];
      var entry = p.colors.find(function (c) { return c.color === chosen; });
      var img = (entry && entry.image) || p.image || '';

      var swatches = p.colors.map(function (c) {
        var already = state.garments.some(function (g) {
          return g.productId === p.id && g.color === c.color;
        });
        var cls = 'blv-prod__swatch';
        if (chosen === c.color) cls += ' is-selected';
        if (already) cls += ' is-added';
        if (!c.available) cls += ' is-oos';
        var note = already ? ' — already in your run' : !c.available ? ' — sold out' : '';
          return '<button type="button" class="' + cls + '"' +
          ' style="background:' + swatchFor(c) + '"' +
          ' data-blv-swatch="' + p.id + '" data-color="' + esc(c.color) + '"' +
          (already || !c.available ? ' disabled' : '') +
          ' aria-label="' + esc(c.color + note) + '"' +
          ' title="' + esc(c.color + note) + '"></button>';
      }).join('');

      var unavailable = p.colors.filter(function (c) { return !c.available; });

      return '' +
        '<div class="blv-prod__blank">' +
          '<div class="blv-prod__blankmedia">' +
            (img ? '<img src="' + esc(img) + '" alt="' + esc(p.title) + '" loading="lazy">' : '') +
          '</div>' +
          '<h4 class="blv-prod__blankname">' + esc(p.title) + '</h4>' +
          (p.spec ? '<p class="blv-prod__blankspec">' + esc(p.spec) + '</p>' : '') +
          '<p class="blv-prod__blanklabel">' +
            (chosen ? esc(chosen) : p.colors.length + ' colors') +
          '</p>' +
          '<div class="blv-prod__swatches">' + swatches + '</div>' +
          (unavailable.length
            ? '<p class="blv-prod__blankoos">' +
                esc(unavailable.map(function (c) { return c.color; }).join(', ')) +
                ' — sold out</p>'
            : '') +
          '<button type="button" class="blv-prod__blankadd" data-blv-pick="' + p.id + '"' +
            (chosen ? '' : ' disabled') + '>' +
            (chosen ? 'Add to run' : 'Choose a color') +
          '</button>' +
        '</div>';
    }).join('');
  }

  function addGarment(productId, color) {
    var product = products.find(function (p) { return String(p.id) === String(productId); });
    if (!product) return;
    var entry = product.colors.find(function (c) { return c.color === color; });
    if (!entry) return;

    var qty = {};
    entry.sizes.forEach(function (s) { qty[s.size] = 0; });

    state.garments.push({
      productId: product.id,
      productTitle: product.title,
      handle: product.handle,
      profile: entry.profile || product.profile || 'tee',
      color: entry.color,
      swatch: entry.swatch || null,

      image: entry.image,
      frontImage: entry.frontImage || entry.image || '',
      backImage: entry.backImage || '',

      sizes: entry.sizes,
      qty: qty
    });

    closeModal(el.picker);
    render();
  }

function readCustomizerHandoff() {
  return new Promise(function (resolve) {
    var request;
    try {
      request = window.indexedDB.open('blvnks', 1);
    } catch (error) {
      resolve(null);
      return;
    }

    request.onupgradeneeded = function () {
      var db = request.result;
      if (!db.objectStoreNames.contains('handoff')) {
        db.createObjectStore('handoff');
      }
    };
    request.onerror = function () { resolve(null); };
    request.onsuccess = function () {
      var db = request.result;
      if (!db.objectStoreNames.contains('handoff')) {
        db.close();
        resolve(null);
        return;
      }
      var get = db.transaction('handoff', 'readonly').objectStore('handoff').get('customizer');
      get.onsuccess = function () {
        db.close();
        resolve(get.result || null);
      };
      get.onerror = function () {
        db.close();
        resolve(null);
      };
    };
  });
}

function applyCustomizerHandoff() {
  var params = new URLSearchParams(window.location.search);
  if (params.get('from') !== 'customizer') return;

  readCustomizerHandoff().then(function (record) {
    if (!record) return;

    var handle = params.get('product');
    if (record.handle && handle && String(record.handle) !== String(handle)) return;

    var files = (record.files || []).map(function (item) {
      if (!item || !item.buffer || !item.name) return null;
      return new File(
        [item.buffer],
        item.name,
        { type: item.type || 'application/octet-stream' }
      );
    }).filter(Boolean);

    if (files.length) addFiles(files);

    var known = {};
    state.artwork.files.forEach(function (file) { known[file.name] = true; });

    if (!state.placements.length && record.placements && record.placements.length) {
      state.placements = record.placements.filter(function (placement) {
        return placement && placement.location && known[placement.file];
      }).map(function (placement) {
        return {
          id: uid(),
          location: placement.location,
          size: placement.location === 'left-chest' ? 'medium' : 'oversized',
          exactWidthIn: null,
          file: placement.file,
          note: '',
          noteAuto: true,
          rotationAdjust: 0
        };
      });
    }

    render();
  });
}

function preloadGarmentFromUrl() {
  var params = new URLSearchParams(window.location.search);

  var handle = params.get('product');
  var requestedColor = params.get('color');

  if (!handle) return;

  var product = products.find(function (p) {
    return String(p.handle) === String(handle);
  });

  if (!product || !product.colors || !product.colors.length) {
    console.warn('[blvnks] preload product not found:', handle);
    return;
  }

  var alreadyAdded = state.garments.some(function (g) {
    return String(g.handle) === String(product.handle);
  });

  if (alreadyAdded) return;

  var entry = null;

  if (requestedColor) {
    entry = product.colors.find(function (c) {
      return (
        String(c.color).toLowerCase() ===
          String(requestedColor).toLowerCase() &&
        c.available
      );
    });
  }

  if (!entry) {
    entry =
      product.colors.find(function (c) {
        return c.available;
      }) ||
      product.colors[0];
  }

  if (!entry) return;

  addGarment(product.id, entry.color);
}

  /* --------------------------------------------------------------- Modals */

  var lastFocus = null;

  function openModal(node) {
    if (!node) return;
    lastFocus = document.activeElement;
    node.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    var focusable = node.querySelector('button, select, input, textarea, a[href]');
    if (focusable) focusable.focus();
  }

  function closeModal(node) {
    if (!node) return;
    node.classList.remove('is-open');
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    root.querySelectorAll('.blv-prod__modal.is-open').forEach(function (m) { closeModal(m); });
  });

  /* --------------------------------------------------------- Review screen */

  function openReview() {
    var verdict = classify();
    var value = estimate() + artworkEstimate();
    var runValue = calculatedRunTotal();
    var payableValue = payableRunTotal();
    var minimumApplies = productionMinimumApplies();
    var units = totalUnits();

    var garmentList = state.garments.map(function (g) {
      return '<li>' + esc(g.productTitle + ' / ' + g.color) + ' — ' + unitsFor(g) + '</li>';
    }).join('');

    var fileList = state.artwork.files.length
      ? state.artwork.files.map(function (f) { return '<li>' + esc(f.name) + '</li>'; }).join('')
      : '<li>No files attached</li>';

    var printList = state.placements.map(function (p) {
      return '<li>' + esc(LOCATIONS[p.location].label + ' — ' + placementSizeLabel(p)) +
        (p.file ? ' <span style="color:var(--blv-muted)">(' + esc(p.file) + ')</span>' : '') +
        (p.note ? '<br><span style="color:var(--blv-muted);font-size:11.5px">' + esc(p.note) + '</span>' : '') +
        '</li>';
    }).join('');

    var priceBox;

    if (state.orderType === 'sample') {
      var sampleList = state.sampleOf.map(function (s) {
        var g = state.garments[s.garment];
        return esc(g.productTitle + ' / ' + g.color + ' — ' + s.size);
      }).join('<br>');

      priceBox = '' +
        '<div class="blv-prod__pricebox">' +
          '<strong>' + money(sampleTotal()) + '</strong>' +
          '<p>' + state.sampleOf.length +
          (state.sampleOf.length === 1 ? ' sample' : ' samples') + ', charged today:<br>' +
          '<span style="color:var(--blv-black)">' + sampleList + '</span><br><br>' +
          (units >= PRICING.dtgQuoteMinQty
            ? 'Your ' + units + '-piece run is saved for custom production pricing. '
            : 'Your ' + units + '-piece run is saved and quoted at ' + money(runValue) + '. ') +
          'Nothing is charged for it until you\'ve seen the samples and told us to go ahead.</p>' +
        '</div>';

    } else if (units >= PRICING.dtgQuoteMinQty) {
      priceBox = '' +
        '<div class="blv-prod__pricebox">' +
          '<strong>Custom production pricing</strong>' +
          '<p>Orders of 50 or more garments are quoted by us. Nothing is charged yet.</p>' +
          '<ul class="blv-prod__flags">' +
            verdict.reasons.map(function (r) {
              return '<li>' + esc(r) + '</li>';
            }).join('') +
          '</ul>' +
        '</div>';

    } else if (verdict.standard) {
      if (minimumApplies) {
        priceBox = '' +
          '<div class="blv-prod__pricebox">' +
            '<strong>' + money(payableValue) + '</strong>' +
            '<p>Your calculated run is ' + money(runValue) +
            '. Production orders have a ' + money(productionMinimum()) +
            ' minimum. This is not an additional setup fee.</p>' +
          '</div>';

      } else {
        priceBox = '' +
          '<div class="blv-prod__pricebox">' +
            '<strong>' + money(payableValue) + '</strong>' +
            '<p>Everything here is standard, so this price is firm. ' +
            'Submit and we\'ll send a payment link straight away.</p>' +
          '</div>';
      }

    } else {
      priceBox = '' +
        '<div class="blv-prod__pricebox">' +
          '<strong>' +
            money(Math.round(runValue * (1 - PRICING.estimateSpread))) +
            ' – ' +
            money(Math.round(runValue * (1 + PRICING.estimateSpread))) +
          '</strong>' +
          '<p>This run needs a look from us before we can price it exactly. Nothing is charged yet.</p>' +
          '<ul class="blv-prod__flags">' +
            verdict.reasons.map(function (r) {
              return '<li>' + esc(r) + '</li>';
            }).join('') +
          '</ul>' +
        '</div>';
    }

    el.reviewBody.innerHTML =
      '<div class="blv-prod__review">' +
        priceBox +
        '<dl>' +
          '<dt>Garments</dt><dd><ul>' + garmentList + '</ul></dd>' +
          '<dt>Total</dt><dd>' + units + ' garments</dd>' +
          dtgReviewRows() +
          '<dt>Artwork</dt><dd><ul>' + fileList + '</ul></dd>' +
          '<dt>Placements</dt><dd><ul>' + printList + '</ul></dd>' +
          (state.artwork.service
            ? '<dt>Artwork service</dt><dd>' + esc(state.artwork.service.title) + ' — ' +
              esc(servicePriceLabel(state.artwork.service)) +
              '<br><span style="color:var(--blv-muted);font-size:11.5px">' +
              'Confirmed and priced at review.</span></dd>'
            : '') +
          '<dt>Order type</dt><dd>' +
            (state.orderType === 'sample' ? 'Sample first' : 'Full production') +
          '</dd>' +
          (state.notes ? '<dt>Notes</dt><dd>' + esc(state.notes) + '</dd>' : '') +
        '</dl>' +
      '</div>';

    el.reviewFoot.innerHTML =
      '<button type="button" class="blv-prod__next" data-blv-confirm style="margin-top:20px">' +
        (verdict.standard ? 'Submit and get payment link' : 'Submit for review') +
        icon('arrow-right', 14) +
      '</button>' +
      '<p class="blv-prod__hint" style="text-align:center;margin-top:12px">' +
        (verdict.standard
          ? 'You\'ll be charged only when you open the payment link.'
          : 'No payment is taken. We\'ll email your quote.') +
      '</p>';

    openModal(el.review);
  }

  function renderSubmitted(result) {
    el.reviewBody.innerHTML =
      '<div class="blv-prod__done">' +
        icon('check-circle', 44) +
        '<h3>Run submitted</h3>' +
        '<p>' + esc(result && result.message
          ? result.message
          : 'We\'ve got your production request. Check your email — we\'ll confirm blank availability, artwork and final pricing shortly.') +
        '</p>' +
      '</div>';
    el.reviewFoot.innerHTML = '';
  }

  /* ------------------------------------------------------------ Submission */

  /* Assemble the payload. Everything your app needs to rebuild and re-price
     the run server-side lives here. Variant IDs are included so the draft
     order can reference real Shopify line items. */
  function buildPayload() {
    var verdict = classify();
    return {
      version: 1,
      submittedAt: new Date().toISOString(),
      contact: {
        email: String(state.contact.email || '').trim(),
        name: String(state.contact.name || '').trim(),
        phone: String(state.contact.phone || '').trim()
      },
      orderType: state.orderType,
      applyToAll: state.applyToAll,
      notes: state.notes,
      totalUnits: totalUnits(),
      sample: state.orderType === 'sample'
        ? {
            variantId: cfg.sampleVariantId || null,
            unitPriceCents: PRICING.samplePrice,
            totalCents: sampleTotal(),
            items: state.sampleOf.map(function (s) {
              var g = state.garments[s.garment];
              var row = g.sizes.find(function (x) { return x.size === s.size; });
              return {
                productTitle: g.productTitle,
                color: g.color,
                size: s.size,
                blankVariantId: row ? row.variantId : null
              };
            })
          }
        : null,
      garments: state.garments.map(function (g) {
        return {
          productId: g.productId,
          productTitle: g.productTitle,
          handle: g.handle,
          profile: g.profile || 'tee',
          color: g.color,
          lines: g.sizes
            .filter(function (s) { return (g.qty[s.size] || 0) > 0; })
            .map(function (s) {
              return { size: s.size, variantId: s.variantId, quantity: g.qty[s.size] };
            })
        };
      }),
      artwork: {
        readiness: state.artwork.readiness,
        /* A requested add-on, not a billed line. Your review decides whether
           this is the right service, which tier it lands on, and what it
           actually costs before it reaches the draft order. */
        requestedService: state.artwork.service
          ? {
              id: state.artwork.service.id,
              title: state.artwork.service.title,
              productId: state.artwork.service.productId || null,
              handle: state.artwork.service.handle || null,
              indicativePriceCents: state.artwork.service.quoteOnly
                ? null
                : state.artwork.service.priceCents,
              quoteOnly: !!state.artwork.service.quoteOnly,
              tier: null
            }
          : null,
          files: state.artwork.files.map(function (f) {
            return {
              name: f.name,
              size: f.size,
              type: f.type,
              url: f.url || null,
              storagePath: f.storagePath || null,
              aspect: f.aspect || null
            };
          })
      },
      placements: state.placements.map(function (p) {
        var s = placementSize(p);
        return {
          id: p.id,
          location: p.location,
          locationLabel: LOCATIONS[p.location].label,
          side: LOCATIONS[p.location].side,
          size: p.size,
          sizeMode: p.exactWidthIn != null ? 'exact' : 'preset',
          exactWidthIn: p.exactWidthIn != null ? placementWidth(p) : null,
          widthIn: s ? s.w : null,
          heightIn: s ? s.h : null,
          sqIn: s ? round2(s.w * s.h) : null,
          file: p.file,
          note: p.note || null,
          proofs: state.placementProofs
            .filter(function (proof) { return proof.placementId === p.id; })
            .map(function (proof) {
              return {
                productId: proof.productId,
                productTitle: proof.productTitle,
                color: proof.color,
                profile: proof.profile,
                url: proof.url || null,
                storagePath: proof.storagePath || null
              };
            })
        };
      }),
      /* Client-side classification and estimate are hints for your queue.
         Recompute both server-side before creating the draft order. */
      clientHints: {
        track: verdict.standard ? 'standard' : 'review',
        reasons: verdict.reasons,

        estimateCents: estimate() + artworkEstimate(),

        calculatedRunTotalCents: calculatedRunTotal(),
        payableRunTotalCents: payableRunTotal(),

        productionMinimumCents: productionMinimum(),
        minimumProductionUnits: minimumProductionUnits(),
        productionQuantityEligible: productionQuantityEligible(),
        productionMinimumApplies: productionMinimumApplies(),

        artworkEstimateCents: artworkEstimate(),

        applications: applicationCount(),
        applicationRateCents: 0,

        blanksCents: blanksEstimate(),
        transfersCents: 0,
        heatPressCents: 0,
        dtgPrintCents: dtgEstimate()
      }
    };
  }

  /* --------------------------------------------------------------------
     Swap point. Point cfg.submitEndpoint at your app proxy and this posts
     the payload there. Shopify signs proxy requests, so your Remix app can
     verify the HMAC before trusting anything in the body.

     Files: this sends metadata only. To handle the actual artwork, have
     your app hand back presigned upload URLs (see uploadFiles below), push
     the files straight to storage from the browser, and let the URLs ride
     along in the payload.
     -------------------------------------------------------------------- */
  function submitRun(payload) {
    if (!cfg.submitEndpoint) {
      console.info('[blvnks] demo mode — payload that would be sent:', payload);
      return Promise.resolve({
        ok: true,
        message: 'Demo mode: this run was assembled but not sent. Set a submit endpoint on the section to go live.'
      });
    }

    return fetch(cfg.submitEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) throw new Error('Server responded ' + res.status);
      return res.json();
    });
  }

  /* Artwork and placement proofs go to Shopify Files through the app proxy,
     the same staged upload the DTF builder and garment customizer use. */
  function shopifyFileUpload(blob, meta) {
    var endpoint = cfg.uploadEndpoint || '/apps/blvnks/dtf';
    var name = (meta && meta.name) || 'artwork.bin';
    var type = (meta && meta.type) || (blob && blob.type) || 'application/octet-stream';

    return fetch(endpoint + '/upload-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        filename: name,
        mimeType: type,
        size: (blob && blob.size) || (meta && meta.size) || 0
      })
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || !data.ok) {
          throw new Error(data.message || 'Artwork upload service is unavailable');
        }
        return data;
      });
    }).then(function (grant) {
      var body;
      var headers = {};

      if (grant.parameters && grant.parameters.length) {
        body = new FormData();
        grant.parameters.forEach(function (param) {
          body.append(param.name, param.value);
        });
        body.append('file', blob, name);
      } else {
        body = blob;
        headers['Content-Type'] = type;
      }

      return fetch(grant.url, {
        method: grant.method || 'POST',
        headers: headers,
        body: body
      }).then(function (response) {
        if (!response.ok) throw new Error('Upload failed for ' + name);
        return fetch(endpoint + '/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            resourceUrl: grant.resourceUrl,
            filename: name,
            mimeType: type,
            kind: 'prod-builder'
          })
        });
      });
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || !data.ok || !data.url) {
          throw new Error(data.message || 'Artwork upload service is unavailable');
        }
        return {
          url: data.url,
          id: data.id || ''
        };
      });
    });
  }

  function uploadFiles() {
    if (!state.artwork.files.length) return Promise.resolve();

    var pending = state.artwork.files.filter(function (f) {
      return f.raw && !f.url;
    });
    if (!pending.length) return Promise.resolve();

    return Promise.all(pending.map(function (f) {
      return shopifyFileUpload(f.raw, {
        name: f.name,
        type: f.type || 'application/octet-stream',
        size: f.size
      }).then(function (stored) {
        f.url = stored.url;
        f.storagePath = stored.id;
        console.log('[blvnks] uploaded artwork', f.name, f.url);
      });
    }));
  }

  function uploadPlacementProofs() {
    var pending = (state.placementProofs || []).filter(function (proof) {
      return proof.blob && !proof.url;
    });
    if (!pending.length) return Promise.resolve();

    return Promise.all(pending.map(function (proof) {
      return shopifyFileUpload(proof.blob, {
        name: proof.name || 'placement-proof.png',
        type: 'image/png',
        size: proof.blob.size
      }).then(function (stored) {
        proof.url = stored.url;
        proof.storagePath = stored.id;
        console.log('[blvnks] uploaded placement proof', proof.name, proof.url);
      });
    }));
  }

  function handleSubmit() {
    if (!canSubmit()) return;
    state.submitting = true;
    state.submitError = null;

    var button = el.reviewFoot.querySelector('[data-blv-confirm]');

    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');

      button.innerHTML =
        '<img class="blv-prod__submit-logo" src="' + esc(cfg.loaderUrl) + '" alt="">' +
        '<span>Submitting run...</span>';
    }

    generatePlacementProofs()
      .then(function () { return uploadFiles(); })
      .then(function () { return uploadPlacementProofs(); })
      .then(function () { return submitRun(buildPayload()); })
      .then(function (result) {
        state.submitted = result;
        /* Standard runs and samples come back with a payment link. Review-track
           runs don't, and get the confirmation screen instead. */
        if (result && result.invoiceUrl) {
          window.location.href = result.invoiceUrl;
          return;
        }
        renderSubmitted(result);
      })
      .catch(function (err) {
        console.error('[blvnks] submit failed', err);
        var msg = document.createElement('p');
        msg.className = 'blv-prod__error';
        msg.textContent = 'That didn\'t go through. Check your connection and try again — nothing was submitted, and your run is still here.';
        el.reviewFoot.appendChild(msg);
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');

          button.innerHTML =
            '<span>Try again</span>' +
            icon('arrow-right', 14);
        }
      })
      .then(function () {
        state.submitting = false;
      });
  }

  /* --------------------------------------------------------- File handling */

  function sampleInkColor(image) {
    try {
      var canvas = document.createElement('canvas');
      canvas.width = 48;
      canvas.height = 48;
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return null;
      ctx.clearRect(0, 0, 48, 48);
      ctx.drawImage(image, 0, 0, 48, 48);
      var data = ctx.getImageData(0, 0, 48, 48).data;
      var r = 0, g = 0, b = 0, weight = 0;
      for (var i = 0; i < data.length; i += 4) {
        var a = data[i + 3] / 255;
        if (a < 0.15) continue;
        r += data[i] * a;
        g += data[i + 1] * a;
        b += data[i + 2] * a;
        weight += a;
      }
      if (!weight) return null;
      function h(v) { return Math.max(0, Math.min(255, Math.round(v / weight))).toString(16).padStart(2, '0'); }
      return '#' + h(r) + h(g) + h(b);
    } catch (err) {
      return null;
    }
  }

  function addFiles(list) {
    var PREVIEWABLE = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml'];
    var accepted = (cfg.acceptedTypes || '').split(',').map(function (s) {
      return s.trim().toLowerCase();
    }).filter(Boolean);
    var maxBytes = (cfg.maxFileMb || 100) * 1048576;

    function finishPreview(entry, previewUrl, knownAspect) {
      if (!entry || !previewUrl) return;

      entry.preview = previewUrl;
      if (knownAspect && isFinite(Number(knownAspect)) && Number(knownAspect) > 0) {
        entry.aspect = Number(knownAspect);
      }

      var probe = new Image();
      probe.onload = function () {
        if (!entry.aspect && probe.naturalWidth && probe.naturalHeight) {
          entry.aspect = probe.naturalWidth / probe.naturalHeight;
        }
        entry.inkColor = sampleInkColor(probe);
        render();
      };
      probe.onerror = function () {
        render();
      };
      probe.src = previewUrl;
    }

    Array.prototype.forEach.call(list, function (file) {
      var ext = '.' + file.name.split('.').pop().toLowerCase();
      if (accepted.length && accepted.indexOf(ext) === -1) {
        alert(file.name + ' isn\'t a file type we can print from. Accepted: ' + accepted.join(', '));
        return;
      }
      if (file.size > maxBytes) {
        alert(file.name + ' is larger than ' + cfg.maxFileMb + 'MB. Send us a link instead and note it below.');
        return;
      }

      var entry = {
        name: file.name,
        size: file.size,
        type: file.type,
        raw: file,
        url: null,
        preview: null,
        aspect: null,
        inkColor: null
      };

      state.artwork.files.push(entry);

      if (file.type === 'image/svg+xml' || ext === '.svg') {
        var normalizeSvg =
          window.BlvnksArtworkEditor &&
          window.BlvnksArtworkEditor.normalizeSvgPreview;

        if (typeof normalizeSvg === 'function') {
          normalizeSvg(
            file,
            function (normalizedUrl, aspect) {
              /* If normalization is impossible, fall back to the browser's
                 normal SVG rendering so the upload remains usable. */
              if (normalizedUrl) {
                finishPreview(
                  entry,
                  normalizedUrl,
                  aspect
                );
              } else {
                finishPreview(
                  entry,
                  URL.createObjectURL(file),
                  null
                );
              }
            }
          );
        } else {
          finishPreview(
            entry,
            URL.createObjectURL(file),
            null
          );
        }
      } else if (PREVIEWABLE.indexOf(file.type) !== -1) {
        finishPreview(entry, URL.createObjectURL(file), null);
      }
    });

    render();
  }

  /* -------------------------------------------------------------- Wiring */

  root.addEventListener('click', function (e) {
    var t = e.target.closest('button, [data-blv-tab]');
    if (!t) return;

    /* tracker */
    if (t.hasAttribute('data-blv-tab')) {
      var n = parseInt(t.dataset.blvTab, 10);
      if (n <= state.reached) goTo(n);
      return;
    }

    if (t.hasAttribute('data-blv-edit')) { goTo(parseInt(t.dataset.blvEdit, 10)); return; }
    if (t.hasAttribute('data-blv-next')) { advance(parseInt(t.dataset.blvNext, 10)); return; }

    /* garments */
    if (t.hasAttribute('data-blv-add-garment')) { openPicker(); return; }
        if (t.hasAttribute('data-blv-filter')) {
      pickerState.filter = t.dataset.blvFilter;
      renderPicker();
      return;
    }
    if (t.hasAttribute('data-blv-swatch')) {
      pickerState.color[t.dataset.blvSwatch] = t.dataset.color;
      renderPicker();
      return;
    }
    if (t.hasAttribute('data-blv-pick')) {
      var pickId = t.dataset.blvPick;
      var pickColor = pickerState.color[pickId];
      if (pickColor) addGarment(pickId, pickColor);
      return;
    }
    if (t.hasAttribute('data-blv-remove-garment')) {
      state.garments.splice(parseInt(t.dataset.blvRemoveGarment, 10), 1);
      /* sampleOf holds garment indices, which shift on removal. Clearing is
         cruder than remapping but it can't silently sample the wrong shirt. */
      state.sampleOf = [];
      render();
      return;
    }

    if (t.dataset.blvQty === 'inc' || t.dataset.blvQty === 'dec') {
      var g = state.garments[parseInt(t.dataset.g, 10)];
      if (!g) return;
      var size = t.dataset.size;
      var next = (g.qty[size] || 0) + (t.dataset.blvQty === 'inc' ? 1 : -1);
      g.qty[size] = Math.max(0, next);
      render();
      return;
    }

    /* artwork */
    if (t.hasAttribute('data-blv-choose')) { el.input.click(); return; }
    if (t.hasAttribute('data-blv-remove-file')) {
    var removed = state.artwork.files.splice(parseInt(t.dataset.blvRemoveFile, 10), 1)[0];
    if (removed && removed.preview) URL.revokeObjectURL(removed.preview);
    state.placements.forEach(function (p) {
      if (removed && p.file === removed.name) p.file = null;
    });
      render();
      return;
    }
    if (t.hasAttribute('data-blv-readiness')) {
      state.artwork.readiness = t.dataset.blvReadiness;
      /* Backing out of "No" shouldn't leave a stale service request attached. */
      if (state.artwork.readiness !== 'help') state.artwork.service = null;
      render();
      return;
    }
    if (t.hasAttribute('data-blv-service')) {
      var picked = services.find(function (s) { return s.id === t.dataset.blvService; });
      state.artwork.service = picked || null;
      render();
      return;
    }

    /* placements */
    if (t.hasAttribute('data-blv-add-placement')) {
      state.placements.push({
        id: uid(),
        location: 'left-chest',
        size: 'medium',
        exactWidthIn: null,
        file: null,
        note: '',
        noteAuto: true,
        rotationAdjust: 0
      });
      render();
      return;
    }
    if (t.hasAttribute('data-blv-remove-placement')) {
      state.placements.splice(parseInt(t.dataset.blvRemovePlacement, 10), 1);
      render();
      return;
    }
    if (t.hasAttribute('data-blv-pviewmode')) {
      var pvm = state.placements[parseInt(t.dataset.p, 10)];
      if (pvm) applyPlacementView(pvm, t.dataset.blvPviewmode);
      render();
      return;
    }
    if (t.hasAttribute('data-blv-pview')) {
      var pv = state.placements[parseInt(t.dataset.blvPview, 10)];
      if (pv) pv.previewKey = t.dataset.viewKey || null;
      renderPlacements();
      return;
    }
    if (t.hasAttribute('data-blv-psize')) {
      var pl = state.placements[parseInt(t.dataset.p, 10)];
      if (pl) {
        pl.size = t.dataset.blvPsize;
        pl.exactWidthIn = null;
        refreshAutoCustomNote(pl);
      }
      render();
      return;
    }
    if (t.hasAttribute('data-blv-pwidth-step')) {
      var pw = state.placements[parseInt(t.dataset.p, 10)];
      if (pw) {
        var delta = t.dataset.blvPwidthStep === 'up' ? EXACT_WIDTH_STEP_IN : -EXACT_WIDTH_STEP_IN;
        setPlacementWidth(pw, placementWidth(pw) + delta);
      }
      render();
      return;
    }

    if (t.hasAttribute('data-blv-protate-step')) {
      var pr = state.placements[parseInt(t.dataset.p, 10)];
      if (pr) {
        nudgePlacementRotation(
          pr,
          Number(t.dataset.blvProtateStep) || 0
        );
      }
      renderPlacements();
      return;
    }
    if (t.hasAttribute('data-blv-protate-reset')) {
      var prr = state.placements[parseInt(t.dataset.p, 10)];
      if (prr) resetPlacementRotation(prr);
      renderPlacements();
      return;
    }
    if (t.hasAttribute('data-blv-scope-toggle')) {
      state.applyToAll = !state.applyToAll;
      render();
      return;
    }

    /* order type */
    if (t.hasAttribute('data-blv-path')) {

      if (
        t.dataset.blvPath === 'production' &&
        !productionQuantityEligible()
      ) {
        state.orderType = null;
        state.sampleOf = [];
        render();
        return;
      }

      state.orderType = t.dataset.blvPath;

      if (state.orderType !== 'sample') {
        state.sampleOf = [];
      } else if (!state.sampleOf.length && state.garments.length === 1) {
        /* One garment means there's nothing to choose. Pick it for them. */
        state.sampleOf = [{
          garment: 0,
          size: dominantSize(state.garments[0])
        }];
      }

      render();
      return;
    }
    if (t.hasAttribute('data-blv-sample-toggle')) {
      var sgi = parseInt(t.dataset.blvSampleToggle, 10);
      var at = state.sampleOf.findIndex(function (s) { return s.garment === sgi; });
      if (at === -1) {
        state.sampleOf.push({ garment: sgi, size: dominantSize(state.garments[sgi]) });
      } else {
        state.sampleOf.splice(at, 1);
      }
      render();
      return;
    }

    /* modals + submission */
    if (t.hasAttribute('data-blv-close')) { closeModal(t.closest('.blv-prod__modal')); return; }
    if (t.hasAttribute('data-blv-submit')) { openReview(); return; }
    if (t.hasAttribute('data-blv-confirm')) { handleSubmit(); return; }
  });

  root.addEventListener('change', function (e) {
    var t = e.target;

    if (t.dataset.blvQty === 'set') {
      var g = state.garments[parseInt(t.dataset.g, 10)];
      if (!g) return;
      g.qty[t.dataset.size] = Math.max(0, parseInt(t.value, 10) || 0);
      render();
      return;
    }
    if (t.hasAttribute('data-blv-ploc')) {
      var p = state.placements[parseInt(t.dataset.p, 10)];
      if (p) {
        var requested = canonicalLocationKey(t.value);
        var requestedLoc = LOCATIONS[requested];

        p.location = requested === 'left-chest'
          ? 'left-chest'
          : (requestedLoc && requestedLoc.view === 'back' ? 'custom-back' : 'custom-front');

        p.customPos = null;
        p.sleeveT = null;
        p.rotationAdjust = 0;
        p.editor = null;
        p.noteAuto = true;
        normalizePlacementWidth(p);
      }
      render();
      return;
    }
    if (t.hasAttribute('data-blv-pfile')) {
      var pf = state.placements[parseInt(t.dataset.p, 10)];
      if (pf) {
        pf.file = t.value || null;
        normalizePlacementWidth(pf);
      }
      render();
      return;
    }
    if (t.hasAttribute('data-blv-pwidth')) {
      var pwidth = state.placements[parseInt(t.dataset.p, 10)];
      if (pwidth) setPlacementWidth(pwidth, t.value);

      /* Width changes the DTG tier, so use the full render path here. */
      render();
      return;
    }
        if (t.hasAttribute('data-blv-sample-size')) {
      var ssgi = parseInt(t.dataset.blvSampleSize, 10);
      var entry = state.sampleOf.find(function (s) { return s.garment === ssgi; });
      if (entry) entry.size = t.value;
      render();
      return;
    }
    if (t === el.input) {
      addFiles(t.files);
      t.value = '';
      return;
    }
  });

  root.addEventListener('input', function (e) {
    if (!e.target.hasAttribute) return;

    if (e.target.hasAttribute('data-blv-pnote')) {
      var pn = state.placements[parseInt(e.target.dataset.p, 10)];
      if (pn) {
        pn.note = e.target.value;
        pn.noteAuto = false;
      }
      return;
    }

    if (e.target.hasAttribute('data-blv-contact')) {
      state.contact[e.target.dataset.blvContact] = e.target.value;
      /* Email gates submission, so the submit button has to react as they
         type. The contact inputs aren't re-rendered, so this won't steal focus. */
      if (e.target.dataset.blvContact === 'email') {
        root.querySelectorAll('[data-blv-submit]').forEach(function (b) {
          b.disabled = !canSubmit();
        });
      }
    }
  });

  /* Desktop-only drag for Front/Back Custom placements. The browser keeps
     SVG coordinates internally, but the operator-facing payload remains a plain
     English note such as "centered, 4.5 in below collar". */
  var dragState = null;

  function desktopDragAvailable() {
    return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches && window.innerWidth > 700;
  }

  function customPlacementNote(p, centerX, topY, profile) {
    var loc = LOCATIONS[p.location];
    var side = previewSideConfig(profile, loc);
    var unitsPerInch = previewUnitsPerInch(profile, loc);
    var collarY = side && isFinite(Number(side.collarY))
      ? Number(side.collarY)
      : profile.collarY;

    var horizontalIn = (centerX - 50) / unitsPerInch;
    var belowIn = Math.max(0, (topY - collarY) / unitsPerInch);
    function half(n) { return Math.round(n * 2) / 2; }
    var horizontal = Math.abs(horizontalIn) < 0.5
      ? 'centered'
      : half(Math.abs(horizontalIn)).toFixed(1).replace('.0', '') + ' in ' + (horizontalIn < 0 ? 'left' : 'right') + ' of center';
    return horizontal + ', ' + half(belowIn).toFixed(1).replace('.0', '') + ' in below collar';
  }

  function sleevePlacementNote(p, centerY, rotation, profile) {
    normalizePlacementLocation(p);
    var loc = LOCATIONS[p.location];
    var track = sleeveTrack(profile, loc, p.location);
    var t = isFinite(Number(p.sleeveT)) ? clamp01(Number(p.sleeveT)) : 0.5;

    var zone = t < 0.33
      ? 'upper sleeve'
      : (t < 0.67 ? 'mid sleeve' : 'lower sleeve');

    return loc.label + ', ' + zone + ', angle ' + Math.round(rotation) + '°';
  }

  function refreshAutoCustomNote(p) {
    normalizePlacementLocation(p);
    if (!p || (!isCustomLocationKey(p.location) && !isSleeveLocationKey(p.location))) return;
    if (!p.noteAuto) return;

    var garment = viewerGarment(p);
    var profile = garmentProfile(garment && garment.profile);
    var file = state.artwork.files.find(function (f) { return f.name === p.file; });
    var geo = canvasGeometry(p, file, garment);

    if (isCustomLocationKey(p.location)) {
      p.note = customPlacementNote(p, geo.cx, geo.y, profile);
      return;
    }

    if (isSleeveLocationKey(p.location)) {
      p.note = sleevePlacementNote(p, geo.cy, geo.rotation, profile);
    }
  }

  function nudgeSleeveRotation(p, delta) {
    if (!p || !isSleeveLocationKey(p.location)) return;
    var garment = viewerGarment(p);
    var profile = garmentProfile(garment && garment.profile);
    var loc = LOCATIONS[p.location];
    var placement = previewPlacementConfig(profile, loc, p.location);
    var info = sleeveRotationInfo(p, profile, loc, placement);
    var next = Math.max(info.min, Math.min(info.max, info.value + delta));
    p.rotationAdjust = next - info.base;
    refreshAutoCustomNote(p);
  }

  function resetSleeveRotation(p) {
    if (!p || !isSleeveLocationKey(p.location)) return;
    p.rotationAdjust = 0;
    refreshAutoCustomNote(p);
  }

  root.addEventListener('pointerdown', function (e) {
    var group = e.target.closest && e.target.closest('[data-blv-drag-art]');
    if (!group || !desktopDragAvailable()) return;
    var idx = parseInt(group.dataset.blvDragArt, 10);
    var p = state.placements[idx];
    if (!p || !isPlacementDraggableKey(p.location)) return;
    var svg = group.closest('svg');
    if (!svg) return;
    e.preventDefault();
    dragState = { index: idx, group: group, svg: svg };
    group.classList.add('is-dragging');
  });

  window.addEventListener('pointermove', function (e) {
    if (!dragState) return;
    var p = state.placements[dragState.index];
    if (!p) return;

    var garment = viewerGarment(p);
    var profile = garmentProfile(garment && garment.profile);
    var file = state.artwork.files.find(function (f) { return f.name === p.file; });
    var loc = LOCATIONS[p.location];

    var pt = dragState.svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    var matrix = dragState.svg.getScreenCTM();
    if (!matrix) return;
    var local = pt.matrixTransform(matrix.inverse());

    if (isSleeveLocationKey(p.location)) {
      var track = sleeveTrack(profile, loc, p.location);
      if (!track) return;

      p.sleeveT = projectPointToSleeveTrack(track, local.x, local.y);
      var sleeveGeo = canvasGeometry(p, file, garment);

      dragState.group.setAttribute('transform', artGroupTransform(sleeveGeo));
      p.note = sleevePlacementNote(p, sleeveGeo.cy, sleeveGeo.rotation, profile);
      p.noteAuto = true;
    } else {
      var geo = canvasGeometry(p, file, garment);
      var region = placementRegion(profile, loc, p.location);
      if (!region) return;

      var minX = region.x + geo.w / 2;
      var maxX = region.x + region.w - geo.w / 2;
      var minY = region.y + geo.h / 2;
      var maxY = region.y + region.h - geo.h / 2;

      var cx = Math.max(minX, Math.min(maxX, local.x));
      var cy = Math.max(minY, Math.min(maxY, local.y));
      p.customPos = { x: cx, y: cy };

      geo.cx = cx;
      geo.cy = cy;
      geo.x = cx - geo.w / 2;
      geo.y = cy - geo.h / 2;
      geo.rotation = 0;

      dragState.group.setAttribute('transform', artGroupTransform(geo));
      p.note = customPlacementNote(p, cx, geo.y, profile);
      p.noteAuto = true;
    }

    var input = root.querySelector('[data-blv-pnote][data-p="' + dragState.index + '"]');
    if (input) input.value = p.note;
  });

  function endPlacementDrag() {
    if (!dragState) return;
    dragState.group.classList.remove('is-dragging');
    dragState = null;
  }

  window.addEventListener('pointerup', endPlacementDrag);
  window.addEventListener('pointercancel', endPlacementDrag);

  if (el.notes) {
    el.notes.addEventListener('input', function () {
      state.notes = el.notes.value;
    });
  }

  /* drag and drop */
  if (el.drop) {
    ['dragenter', 'dragover'].forEach(function (evt) {
      el.drop.addEventListener(evt, function (e) {
        e.preventDefault();
        el.drop.classList.add('is-over');
      });
    });
    ['dragleave', 'drop'].forEach(function (evt) {
      el.drop.addEventListener(evt, function (e) {
        e.preventDefault();
        el.drop.classList.remove('is-over');
      });
    });
    el.drop.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files) addFiles(e.dataTransfer.files);
    });
  }

  preloadGarmentFromUrl();
  applyCustomizerHandoff();

  if (!state.garments.length) {
    render();
  }
  })();